import type { ConstructionPaymentState } from './types';
import type { BankCaptureResult } from "./bank-capture-types";
import type { ApprovalMarginAllocation } from "@totalbot/domain";
import type { QuoteCustomerGroup } from '@totalbot/domain/quote-grouping';
import type { TaxCashReport, TaxReviewCommand } from '@totalbot/domain';
import type { CardCreateInput, CardTransaction, CardCancellationInput, CardSettlementInput } from "./card-types";
import type { AsExpenseCase, AsExpenseCreateInput, AsExpenseCostInput, AsExpenseList, AsMarginPreview, AsMarginPreviewInput } from "./as-expense-types";
import type {
  AppUser,
  AuthSession,
  CalendarAdminStatus,
  CalendarConnectionStatus,
  CompanyQuoteProfile,
  CompletedQuoteApprovalPage,
  CreateAppUserInput,
  CreateCustomerReceiptInput,
  CreateProjectInput,
  CreateQuoteInput,
  CreateVendorPaymentRequestInput,
  CustomerRefundApprovalQueue,
  CustomerRefundClaim,
  CustomerReceipt,
  CustomerReceiptPage,
  DashboardOverview,
  DashboardSchedule,
  DashboardScheduleAttention,
  DashboardSummary,
  DeviceDeliveryMonitoringView,
  EmployeeSettlementReport,
  HealthStatus,
  ImprovementPrincipal,
  ImprovementRequest,
  ImprovementStatus,
  IntegratedPilotProjectPage,
  IntegratedShellAccess,
  HomeStyleProjectPage,
  HomeStyleProjectDto,
  HomeStyleProjectMutation,
  HomeStyleConstructionCreateResult,
  HomeStylePrivateProjectDetail,
  HomeStyleOriginalSourceStatus,
  HomeStyleOriginalSourceDetail,
  HomeStyleFieldWorkPage,
  HomeStyleFieldWorkProjectDetail,
  HomeStyleFieldWorkStageCode,
  HomeStyleFieldWorkCompleteInput,
  HomeStyleEditSession,
  HomeStylePurchaseListTemplate,
  HomeStyleWorkflowView,
  HomeStyleWorkflowStageCode,
  HomeStyleStageCompleteCommand,
  HomeStyleWorkflowCommand,
  HomeStyleScheduleStageCode,
  HomeStyleScheduleRuleSnapshot,
  HomeStyleScheduleRuleState,
  HomeStyleBalanceDatePreview,
  HomeStyleMediaView,
  HomeStyleActiveResourcePreview,
  HomeStyleMediaAssetDto,
  HomeStyleExistingMediaCandidateDto,
  GuestHomeStyleProjectDto,
  GuestHomeStyleSession,
  GuestInviteConsumption,
  GuestIntakeSubmissionInput,
  GuestPortalRuntimeConfig,
  GuestIntakeField,
  HomeStyleGuestGrantDto,
  HomeStyleGuestStagePublicationDto,
  HomeStyleGuestIntakeSubmissionDto,
  HomeStyleGuestInviteDto,
  ChangeJournalEntry,
  RollbackRequest,
  ManagerOption,
  ManagerCompensationRule,
  ManagerSettlement,
  MasterData,
  MasterSummary,
  MonthlyProjectActivity,
  MonthClosing,
  ParsedDepositNotification,
  ProjectDetail,
  ProjectPage,
  ProjectStatus,
  ProjectView,
  ProjectTrashPage,
  QuoteApprovalQueue,
  QuoteItem,
  QuotePdfLinkResult,
  QuoteVersionSummary,
  SaveQuoteItemInput,
  SmsDraftInput,
  SmsMessage,
  SmsMessagePage,
  SmsPrefill,
  SyncStatus,
  SyncJobPage,
  TradeOption,
  UnmatchedDepositNotification,
  UpdateAppUserInput,
  UpdateQuoteItemScheduleInput,
  UpdateProjectInput,
  VendorPaymentObligation,
  VendorPaymentRequest,
  VendorPaymentRequestPage,
  BankPaymentCapabilities,
  BankTransaction,
  BankLedgerTargetType,
  BankTransactionClassification,
  BankTransactionLedger,
  BankPaymentBatch,
  BankMirrorSession,
  BankPaymentOrder,
  BankTransferResolution,
  CompletionPhoto,
  FinanceCapabilities,
  ProjectCompletionPhoto,
  VendorRefundClaim,
  VendorAccountHistoryItem,
  VendorAccountLink,
  VendorOption,
} from "./types";

type RawQuoteItem = QuoteItem & {
  bank?: string;
  finalExpectedMargin?: number;
  originalItem?: QuoteItem["original"];
  originalTradeId?: string;
  originalTradeName?: string;
  originalDetail?: string;
  originalVendorId?: string;
  originalVendorName?: string;
  originalVendorCost?: number;
  originalCalculationMode?: QuoteItem["calculationMode"];
  originalMarginRate?: number;
  originalCustomerAmount?: number;
};
type RawQuote = QuoteVersionSummary & {
  cumulativeAmount?: number;
  changeAmount?: number;
  finalExpectedMargin?: number;
  items?: RawQuoteItem[];
};

function normalizeStatus(value: unknown) {
  return String(value ?? "").toUpperCase();
}

function normalizeQuote(value: RawQuote): QuoteVersionSummary {
  return {
    ...value,
    status: normalizeStatus(value.status),
    revisionKind: value.revisionKind ? normalizeStatus(value.revisionKind) : undefined,
    pdfLinkStatus: value.pdfLinkStatus ? normalizeStatus(value.pdfLinkStatus) as QuoteVersionSummary["pdfLinkStatus"] : undefined,
    pdfStorageLocation: value.pdfStorageLocation ? normalizeStatus(value.pdfStorageLocation) as QuoteVersionSummary["pdfStorageLocation"] : undefined,
    pdfArchiveStatus: value.pdfArchiveStatus ? normalizeStatus(value.pdfArchiveStatus) as QuoteVersionSummary["pdfArchiveStatus"] : undefined,
    pdfDocumentStatus: value.pdfDocumentStatus ? normalizeStatus(value.pdfDocumentStatus) as QuoteVersionSummary["pdfDocumentStatus"] : undefined,
    pdfGenerationStatus: value.pdfGenerationStatus ? normalizeStatus(value.pdfGenerationStatus) as QuoteVersionSummary["pdfGenerationStatus"] : undefined,
    customerTotal: value.customerTotal ?? value.cumulativeAmount ?? 0,
    cumulativeAmount: value.cumulativeAmount ?? value.customerTotal ?? 0,
    changeAmount: value.changeAmount ?? 0,
    expectedMargin: value.expectedMargin ?? value.finalExpectedMargin ?? 0,
    items: value.items?.map((item) => {
      const raw = item as RawQuoteItem;
      const original = raw.original ?? raw.originalItem ?? (raw.originalItemId ? {
        id: raw.originalItemId,
        tradeId: raw.originalTradeId,
        tradeName: raw.originalTradeName,
        detail: raw.originalDetail,
        vendorId: raw.originalVendorId,
        vendorName: raw.originalVendorName,
        vendorCost: raw.originalVendorCost,
        calculationMode: raw.originalCalculationMode,
        marginRate: raw.originalMarginRate,
        customerAmount: raw.originalCustomerAmount,
      } : undefined);
      return {
        ...item,
        calculationMode: normalizeStatus(item.calculationMode),
        changeType: normalizeStatus(item.changeType),
        status: item.status ? normalizeStatus(item.status) : undefined,
        bankName: item.bankName ?? raw.bank,
        expectedMargin: item.expectedMargin ?? raw.finalExpectedMargin,
        original,
      };
    }),
  };
}

function normalizeProject(value: ProjectDetail): ProjectDetail {
  return {
    ...value,
    quoteVersions: value.quoteVersions?.map((quote) => normalizeQuote(quote as RawQuote)),
  };
}

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
  touchedAt: number;
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  cacheTtlMs?: number;
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${stableJson(item)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value) ?? String(value);
}

const FINANCE_COMMAND_STORAGE_PREFIX = "chongmu.finance-command.v1.";
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

async function sha256Hex(bytes: BufferSource): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function homeStyleMediaIdempotencyKey(projectId: string, stageCode: string, file: File): Promise<string> {
  const metadata = new TextEncoder().encode([
    "project-selmo-media-v1",
    projectId,
    stageCode,
    file.name,
    file.type,
    file.size,
    file.lastModified,
  ].join("\n"));
  const head = new Uint8Array(await file.slice(0, 64 * 1024).arrayBuffer());
  const fingerprint = new Uint8Array(metadata.length + head.length);
  fingerprint.set(metadata);
  fingerprint.set(head, metadata.length);
  return `media-v1-${await sha256Hex(fingerprint)}`;
}

async function financeCommandFingerprint(value: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class ApiClient {
  private readonly cache = new Map<string, CacheEntry<unknown>>();
  private readonly inflight = new Map<string, Promise<unknown>>();
  private readonly financialCommandKeys = new Map<string, string>();
  private cacheRevision = 0;

  constructor(
    private readonly baseUrl: string,
    private readonly maxCacheEntries = 40,
  ) {}

  private url(path: string) {
    const root = this.baseUrl.replace(/\/$/, "");
    return `${root}${path.startsWith("/") ? path : `/${path}`}`;
  }

  private cacheKey(path: string) {
    return path;
  }

  private readCache<T>(key: string): T | undefined {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    if (!entry) return undefined;
    if (entry.expiresAt <= Date.now()) {
      this.cache.delete(key);
      return undefined;
    }
    entry.touchedAt = Date.now();
    return entry.value;
  }

  private writeCache<T>(key: string, value: T, ttlMs: number) {
    if (this.cache.size >= this.maxCacheEntries && !this.cache.has(key)) {
      const oldest = [...this.cache.entries()].sort((a, b) => a[1].touchedAt - b[1].touchedAt)[0];
      if (oldest) this.cache.delete(oldest[0]);
    }
    const now = Date.now();
    this.cache.set(key, { value, expiresAt: now + ttlMs, touchedAt: now });
  }

  invalidate(prefix = "") {
    this.cacheRevision += 1;
    const matches = (key: string) =>
      key.startsWith(prefix)
      || (prefix === "/dashboard" && key.startsWith("/manager-settlements/report"));
    for (const key of this.cache.keys()) {
      if (matches(key)) this.cache.delete(key);
    }
    for (const key of this.inflight.keys()) {
      if (matches(key)) this.inflight.delete(key);
    }
  }

  private waitForSignal<T>(promise: Promise<T>, signal?: AbortSignal | null): Promise<T> {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(new DOMException("The operation was aborted.", "AbortError"));
    return new Promise<T>((resolve, reject) => {
      const abort = () => reject(new DOMException("The operation was aborted.", "AbortError"));
      signal.addEventListener("abort", abort, { once: true });
      promise.then(
        (value) => {
          signal.removeEventListener("abort", abort);
          resolve(value);
        },
        (error) => {
          signal.removeEventListener("abort", abort);
          reject(error);
        },
      );
    });
  }

  private query(path: string, values: Record<string, string | number | boolean | undefined>) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(values)) {
      if (value !== undefined && value !== "") params.set(key, String(value));
    }
    const query = params.toString();
    return query ? `${path}?${query}` : path;
  }

  private items<T>(value: T[] | { items?: T[] }) {
    return Array.isArray(value) ? value : (value.items ?? []);
  }

  private storedFinancialCommandKey(commandFingerprint: string): string | undefined {
    const memory = this.financialCommandKeys.get(commandFingerprint);
    if (memory) return memory;
    try {
      const stored = globalThis.sessionStorage?.getItem(
        `${FINANCE_COMMAND_STORAGE_PREFIX}${commandFingerprint}`,
      );
      if (stored && UUID_PATTERN.test(stored)) {
        this.financialCommandKeys.set(commandFingerprint, stored);
        return stored;
      }
      if (stored) {
        globalThis.sessionStorage?.removeItem(
          `${FINANCE_COMMAND_STORAGE_PREFIX}${commandFingerprint}`,
        );
      }
    } catch {
      // sessionStorage may be unavailable in restricted browser contexts.
      // The in-memory map still keeps retries safe within the current page.
    }
    return undefined;
  }

  private rememberFinancialCommandKey(commandFingerprint: string, key: string): void {
    if (this.financialCommandKeys.size >= 100) {
      const oldest = this.financialCommandKeys.keys().next().value;
      if (oldest) this.financialCommandKeys.delete(oldest);
    }
    this.financialCommandKeys.set(commandFingerprint, key);
    try {
      globalThis.sessionStorage?.setItem(
        `${FINANCE_COMMAND_STORAGE_PREFIX}${commandFingerprint}`,
        key,
      );
    } catch {
      // See storedFinancialCommandKey: memory-only is the safe fallback.
    }
  }

  private forgetFinancialCommandKey(commandFingerprint: string): void {
    this.financialCommandKeys.delete(commandFingerprint);
    try {
      globalThis.sessionStorage?.removeItem(
        `${FINANCE_COMMAND_STORAGE_PREFIX}${commandFingerprint}`,
      );
    } catch {
      // Nothing else to clear when browser storage is unavailable.
    }
  }

  private async financialRequest<T>(path: string, options: RequestOptions): Promise<T> {
    const method = (options.method ?? "POST").toUpperCase();
    const commandFingerprint = await financeCommandFingerprint(
      `${method}\n${path}\n${stableJson(options.body)}`,
    );
    let idempotencyKey = this.storedFinancialCommandKey(commandFingerprint);
    if (!idempotencyKey) {
      idempotencyKey = globalThis.crypto.randomUUID();
      this.rememberFinancialCommandKey(commandFingerprint, idempotencyKey);
    }
    const headers = new Headers(options.headers);
    headers.set("Idempotency-Key", idempotencyKey);
    try {
      const value = await this.request<T>(path, { ...options, headers });
      this.forgetFinancialCommandKey(commandFingerprint);
      return value;
    } catch (error) {
      // A lost response can leave the caller unsure whether money was recorded.
      // Retain the key so an exact-payload retry asks the server for the same result.
      // A definitive client error means the server rejected this attempt; discard
      // the key so the same corrected business action is not pinned to FAILED.
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        const code = (
          typeof error.details === "object"
          && error.details !== null
          && "code" in error.details
        ) ? String(error.details.code) : "";
        if (code !== "FINANCE_COMMAND_RESULT_UNKNOWN") {
          this.forgetFinancialCommandKey(commandFingerprint);
        }
      }
      throw error;
    }
  }

  taxReconciliation(month:string,signal?:AbortSignal) {
    return this.request<TaxCashReport>(this.query('/tax-reconciliation',{month}),{signal});
  }
  reviewTaxReconciliation(command:TaxReviewCommand) {
    return this.request<TaxCashReport>('/tax-reconciliation/review',{method:'POST',body:command});
  }
  cards(query: {kind?: string; projectId?: string; paymentRequestId?: string; trash?: string; month?: string; cursor?: string} = {}, signal?: AbortSignal) {
    return this.request<{items:CardTransaction[];nextCursor:string|null}>(this.query('/card-transactions',query),{signal});
  }
  card(id:string) { return this.request<CardTransaction>(`/card-transactions/${encodeURIComponent(id)}`); }
  async createCard(input:CardCreateInput) {
    const result=await this.financialRequest<CardTransaction>('/card-transactions',{method:'POST',body:input});this.invalidate();return result;
  }
  async cardAction(id:string,action:'confirm'|'trash'|'restore'|'cancel'|'settle'|'reverse-settlement',input:{revision:number}|CardCancellationInput|CardSettlementInput|{revision:number;settlementId:string;reason:string}) {
    const result=await this.financialRequest<CardTransaction>(`/card-transactions/${encodeURIComponent(id)}/${action}`,{method:'POST',body:input});this.invalidate();return result;
  }
  async uploadCardReceipt(id:string,revision:number,file:File) {
    const digest=await sha256Hex(await file.arrayBuffer()),seed=await financeCommandFingerprint(`${id}\n${revision}\n${file.name}\n${digest}`);
    const key=`${seed.slice(0,8)}-${seed.slice(8,12)}-4${seed.slice(13,16)}-a${seed.slice(17,20)}-${seed.slice(20,32)}`;
    const body=new FormData();body.append('revision',String(revision));body.append('file',file);
    const response=await fetch(this.url(`/card-transactions/${encodeURIComponent(id)}/attachments`),{method:'POST',body,credentials:'include',headers:{'Idempotency-Key':key}});
    const payload=await response.json();if(!response.ok)throw new ApiError(payload.message??'영수증을 저장하지 못했습니다.',response.status,payload);
    this.invalidate();return (payload.data??payload) as CardTransaction;
  }

  asExpenses(query: { filter?: "trash" | "approval"; projectId?: string; cursor?: string; limit?: number } = {}, signal?: AbortSignal) {
    return this.request<AsExpenseList>(this.query("/as-expenses", query), { signal });
  }

  asExpense(id: string, signal?: AbortSignal) {
    return this.request<AsExpenseCase>(`/as-expenses/${encodeURIComponent(id)}`, { signal });
  }

  async quoteAllocationPreview(id: string, marginAllocation: ApprovalMarginAllocation, signal?: AbortSignal) {
    return normalizeQuote(await this.request<RawQuote>(`/quotes/${encodeURIComponent(id)}/allocation-preview`, {method: "POST", body: {marginAllocation}, signal}));
  }

  asExpenseMarginPreview(id: string, signal?: AbortSignal, marginBurden?: "SHARED" | "COMPANY", marginAllocation?: ApprovalMarginAllocation) {
    if (marginAllocation) return this.previewAsExpenseMargin({caseId: id, costs: [], marginBurden, marginAllocation}, signal);
    return this.request<AsMarginPreview>(this.query(`/as-expenses/${encodeURIComponent(id)}/margin-preview`, { marginBurden }), { signal });
  }

  previewAsExpenseMargin(input: AsMarginPreviewInput, signal?: AbortSignal) {
    return this.request<AsMarginPreview>("/as-expenses/margin-preview", { method: "POST", body: input, signal });
  }

  async correctAsExpense(id:string,input:import('./as-expense-types').AsExpenseCorrectionInput) {
    const result=await this.financialRequest<AsExpenseCase>(`/as-expenses/${encodeURIComponent(id)}/correct`,{method:'POST',body:input});
    this.invalidate();return result;
  }
  async createAsExpense(input: AsExpenseCreateInput) {
    const result = await this.financialRequest<AsExpenseCase>("/as-expenses", { method: "POST", body: input });
    this.invalidate();
    return result;
  }

  async addAsExpenseCosts(id: string, revision: number, costs: AsExpenseCostInput[]) {
    const result = await this.financialRequest<AsExpenseCase>(`/as-expenses/${encodeURIComponent(id)}/costs`, { method: "POST", body: { revision, costs } });
    this.invalidate();
    return result;
  }

  async confirmAsExpense(id: string, revision: number, previewFingerprint?: string, marginBurden?: "SHARED" | "COMPANY") {
    const result = await this.financialRequest<AsExpenseCase>(`/as-expenses/${encodeURIComponent(id)}/confirm`, { method: "POST", body: { revision, previewFingerprint, marginBurden } });
    this.invalidate();
    return result;
  }

  async asCancellationPreview(id:string) {
    return this.request<import('./as-expense-types').AsCancellationPreview>(`/as-expenses/${encodeURIComponent(id)}/cancellation-preview`);
  }
  async cancelAsExpense(id:string,revision:number,reason:string,fingerprint:string) {
    const result=await this.financialRequest<AsExpenseCase>(`/as-expenses/${encodeURIComponent(id)}/cancel`,{method:'POST',body:{revision,reason,fingerprint}});
    this.invalidate(); return result;
  }

  async transitionAsExpense(id: string, revision: number, action: "approve" | "trash" | "restore", previewFingerprint?: string, marginBurden?: "SHARED" | "COMPANY", marginAllocation?: ApprovalMarginAllocation) {
    const result = await this.financialRequest<AsExpenseCase>(`/as-expenses/${encodeURIComponent(id)}/${action}`, { method: "POST", body: { revision, previewFingerprint, ...(action === "approve" ? { marginBurden, marginAllocation } : {}) } });
    this.invalidate();
    return result;
  }

  async linkAsExpense(id: string, revision: number, projectId: string) {
    const result = await this.financialRequest<AsExpenseCase>(`/as-expenses/${encodeURIComponent(id)}/link`, { method: "POST", body: { revision, projectId } });
    this.invalidate();
    return result;
  }

  async uploadAsExpenseAttachment(id: string, file: File) {
    const digest = await sha256Hex(await file.arrayBuffer());
    const seed = await financeCommandFingerprint(`${id}\n${file.name}\n${digest}`);
    const key = `${seed.slice(0,8)}-${seed.slice(8,12)}-4${seed.slice(13,16)}-a${seed.slice(17,20)}-${seed.slice(20,32)}`;
    const body = new FormData();
    body.append("file", file);
    const response = await fetch(this.url(`/as-expenses/${encodeURIComponent(id)}/attachments`), {
      method: "POST", body, credentials: "include", headers: { "Idempotency-Key": key },
    });
    const payload = await response.json();
    if (!response.ok) throw new ApiError(payload.message ?? "증빙을 보관하지 못했습니다.", response.status, payload);
    const result = (payload.data ?? payload) as AsExpenseCase;
    this.invalidate();
    return result;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const method = (options.method ?? "GET").toUpperCase();
    const key = this.cacheKey(path);
    if (options.signal?.aborted) throw new DOMException("The operation was aborted.", "AbortError");
    if (method === "GET" && options.cacheTtlMs) {
      const cached = this.readCache<T>(key);
      if (cached !== undefined) return cached;
      const pending = this.inflight.get(key) as Promise<T> | undefined;
      if (pending) return this.waitForSignal(pending, options.signal);
    }

    const revisionAtStart = this.cacheRevision;
    const execute = async (requestOptions: RequestOptions) => {
      const value = await this.performRequest<T>(path, requestOptions);
      if (method === "GET" && options.cacheTtlMs && revisionAtStart === this.cacheRevision) {
        this.writeCache(key, value, options.cacheTtlMs);
      }
      return value;
    };

    // Cacheable reads share one underlying request even when each React view
    // owns a different AbortSignal. Aborting one view rejects only that
    // subscriber; it cannot cancel another mounted view's identical request.
    if (method === "GET" && options.cacheTtlMs) {
      const { signal: _signal, ...sharedOptions } = options;
      const promise = execute(sharedOptions);
      this.inflight.set(key, promise);
      const clearInflight = () => {
        if (this.inflight.get(key) === promise) this.inflight.delete(key);
      };
      void promise.then(
        clearInflight,
        clearInflight,
      );
      return this.waitForSignal(promise, options.signal);
    }

    return execute(options);
  }

  private async performRequest<T>(path: string, options: RequestOptions): Promise<T> {

    const headers = new Headers(options.headers);
    headers.set("Accept", "application/json");
    if (options.body !== undefined) headers.set("Content-Type", "application/json");

    let response: Response;
    try {
      response = await fetch(this.url(path), {
        ...options,
        headers,
        credentials: "include",
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      throw new ApiError("서버에 연결할 수 없습니다.", 0, error);
    }

    const contentType = response.headers.get("content-type") ?? "";
    const payload = contentType.includes("application/json")
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      const message =
        typeof payload === "object" && payload && "message" in payload
          ? String(payload.message)
          : `요청을 처리하지 못했습니다. (${response.status})`;
      throw new ApiError(message, response.status, payload);
    }

    const value =
      typeof payload === "object" && payload && "data" in payload
        ? (payload.data as T)
        : (payload as T);

    return value;
  }

  health(signal?: AbortSignal) {
    return this.request<HealthStatus>("/health", { signal, cacheTtlMs: 10_000 });
  }

  financeCapabilities(signal?: AbortSignal) {
    return this.request<FinanceCapabilities>("/finance/capabilities", {
      signal,
      cacheTtlMs: 3_000,
    });
  }

  integratedShellAccess(signal?: AbortSignal) {
    return this.request<IntegratedShellAccess>("/integrated/access", {
      signal,
      cacheTtlMs: 3_000,
    });
  }

  integratedPilotProjects(signal?: AbortSignal) {
    return this.request<IntegratedPilotProjectPage>("/integrated/projects", {
      signal,
      cacheTtlMs: 3_000,
    });
  }

  integratedPilotProject(id: string, signal?: AbortSignal) {
    return this.request<ProjectDetail>(`/integrated/projects/${encodeURIComponent(id)}`, {
      signal,
      cacheTtlMs: 3_000,
    });
  }

  homeStyleProjects(signal?: AbortSignal) {
    return this.request<HomeStyleProjectPage>("/home-style/projects", {
      signal,
      cacheTtlMs: 5_000,
    });
  }

  homeStyleProject(id: string, signal?: AbortSignal) {
    return this.request<HomeStyleProjectDto>(`/home-style/projects/${encodeURIComponent(id)}`, { signal });
  }

  retryHomeStyleGoogleContact(id: string, signal?: AbortSignal) {
    return this.request<HomeStyleProjectDto>(`/home-style/projects/${encodeURIComponent(id)}/google-contact/retry`, {
      method: "POST", signal,
    });
  }

  homeStylePrivateProjectDetail(id: string, signal?: AbortSignal) {
    return this.request<HomeStylePrivateProjectDetail>(
      `/home-style/projects/${encodeURIComponent(id)}/private-detail`,
      { signal },
    );
  }

  homeStyleOriginalSourceStatus(signal?: AbortSignal) {
    return this.request<HomeStyleOriginalSourceStatus>("/home-style/original-source/status",{signal});
  }
  homeStyleOriginalSourceDetail(id:string,signal?:AbortSignal) {
    return this.request<HomeStyleOriginalSourceDetail>(`/home-style/projects/${encodeURIComponent(id)}/original-source`,{signal});
  }

  homeStyleFieldWorkProjects(signal?: AbortSignal) {
    return this.request<HomeStyleFieldWorkPage>("/home-style/field-work/projects", {
      signal,
      cacheTtlMs: 2_000,
    });
  }

  homeStyleFieldWorkDetail(id: string, signal?: AbortSignal) {
    return this.request<HomeStyleFieldWorkProjectDetail>(
      `/home-style/projects/${encodeURIComponent(id)}/field-work-detail`,
      { signal },
    );
  }

  private async homeStyleFieldWorkCommand(
    id: string,
    code: HomeStyleFieldWorkStageCode,
    path: string,
    method: "POST" | "PATCH",
    body: unknown,
    signal?: AbortSignal,
  ) {
    const fingerprint = await sha256Hex(new TextEncoder().encode(stableJson({ id, code, path, body })));
    const headers = new Headers({ "Idempotency-Key": `field-work-v1-${fingerprint}` });
    const detail = await this.request<HomeStyleFieldWorkProjectDetail>(
      `/home-style/projects/${encodeURIComponent(id)}/workflow/stages/${encodeURIComponent(code)}${path}`,
      { method, body, headers, signal },
    );
    this.invalidate("/home-style/field-work");
    return detail;
  }

  updateHomeStyleFieldWorkSchedule(
    id: string,
    code: HomeStyleFieldWorkStageCode,
    input: { expectedVersion: number; scheduledOn: string | null },
    signal?: AbortSignal,
  ) {
    return this.homeStyleFieldWorkCommand(id, code, "/schedule", "PATCH", input, signal);
  }

  restoreHomeStyleFieldWorkSchedule(
    id: string,
    code: HomeStyleFieldWorkStageCode,
    expectedVersion: number,
    signal?: AbortSignal,
  ) {
    return this.homeStyleFieldWorkCommand(id, code, "/schedule/restore-auto", "POST", { expectedVersion }, signal);
  }

  completeHomeStyleFieldWorkStage(
    id: string,
    code: HomeStyleFieldWorkStageCode,
    input: HomeStyleFieldWorkCompleteInput,
    signal?: AbortSignal,
  ) {
    return this.homeStyleFieldWorkCommand(id, code, "/complete", "POST", input, signal);
  }

  correctHomeStyleFieldWorkCompletion(
    id: string,
    code: HomeStyleFieldWorkStageCode,
    input: { expectedVersion: number; completedOn: string; reason: string },
    signal?: AbortSignal,
  ) {
    return this.homeStyleFieldWorkCommand(id, code, "/completion-date", "PATCH", input, signal);
  }

  async createHomeStyleProject(input: Required<Pick<HomeStyleProjectMutation,
    "serviceType" | "customerName" | "roadAddress" | "lotAddress" | "postalCode"
      | "addressNeighborhood" | "contractDate" | "coordinateVerified">>
    & HomeStyleProjectMutation, signal?: AbortSignal) {
    const project = await this.request<HomeStyleProjectDto>("/home-style/projects", {
      method: "POST", body: input, signal,
    });
    this.invalidate();
    return project;
  }

  async updateHomeStyleProject(id: string, input: HomeStyleProjectMutation & { expectedVersion: number }, signal?: AbortSignal) {
    const project = await this.request<HomeStyleProjectDto>(`/home-style/projects/${encodeURIComponent(id)}`, {
      method: "PATCH", body: input, signal,
    });
    this.invalidate();
    return project;
  }

  revealHomeStyleAccessPassword(id: string, signal?: AbortSignal) {
    return this.request<{ password: string | null }>(`/home-style/projects/${encodeURIComponent(id)}/access-password/reveal`, {
      method: "POST", signal,
    });
  }

  homeStylePurchaseListTemplates(signal?: AbortSignal) {
    return this.request<{ items: HomeStylePurchaseListTemplate[] }>("/home-style/purchase-list-templates", { signal });
  }

  async uploadHomeStylePurchaseListTemplate(file: File, signal?: AbortSignal) {
    const body = new FormData();
    body.append("file", file, file.name);
    let response: Response;
    try {
      response = await fetch(this.url("/home-style/purchase-list-templates"), {
        method: "POST", credentials: "include", body, signal, headers: { Accept: "application/json" },
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      throw new ApiError("서버에 연결할 수 없습니다.", 0, error);
    }
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = typeof payload === "object" && payload && "message" in payload
        ? String(payload.message) : "구매리스트 원본을 등록하지 못했습니다.";
      throw new ApiError(message, response.status, payload);
    }
    this.invalidate("/home-style");
    return payload as HomeStylePurchaseListTemplate;
  }

  async retryHomeStyleProvisioning(id: string, signal?: AbortSignal) {
    const project = await this.request<HomeStyleProjectDto>(
      `/home-style/projects/${encodeURIComponent(id)}/provisioning/retry`, { method: "POST", signal },
    );
    this.invalidate("/home-style");
    return project;
  }

  async createHomeStyleConstructionProject(id: string, expectedVersion: number, signal?: AbortSignal) {
    const body = { expectedVersion };
    const fingerprint = await sha256Hex(new TextEncoder().encode(stableJson({
      action: "home-style-construction-create-v1",
      id,
      body,
    })));
    const result = await this.request<HomeStyleConstructionCreateResult>(
      `/home-style/projects/${encodeURIComponent(id)}/construction-project`,
      {
        method: "POST",
        body,
        headers: { "Idempotency-Key": `construction-create-v1-${fingerprint}` },
        signal,
      },
    );
    this.invalidate("/home-style");
    return result;
  }

  homeStyleWorkflow(id: string, signal?: AbortSignal) {
    return this.request<HomeStyleWorkflowView>(
      `/home-style/projects/${encodeURIComponent(id)}/workflow`,
      { signal, cacheTtlMs: 2_000 },
    );
  }

  homeStyleAddressProviderConfig(signal?: AbortSignal) {
    return this.request<{ configured: boolean; kakaoJavascriptKey?: string | null }>(
      "/home-style/address-provider-config",
      { signal, cacheTtlMs: 60_000 },
    );
  }

  homeStyleScheduleRules(signal?: AbortSignal) {
    return this.request<HomeStyleScheduleRuleState>("/home-style/schedule-rules", {
      signal,
      cacheTtlMs: 2_000,
    });
  }

  async publishHomeStyleScheduleRules(
    expectedVersion: number,
    rules: HomeStyleScheduleRuleSnapshot,
    signal?: AbortSignal,
  ) {
    const body = { expectedVersion, rules };
    const fingerprint = await sha256Hex(new TextEncoder().encode(stableJson({ action: "publish-home-style-schedule-rules-v1", body })));
    const state = await this.request<HomeStyleScheduleRuleState>("/home-style/schedule-rules", {
      method: "POST",
      body,
      headers: { "Idempotency-Key": `schedule-rules-v1-${fingerprint}` },
      signal,
    });
    this.invalidate("/home-style/schedule-rules");
    return state;
  }

  private async homeStyleScheduleCommand(
    id: string,
    code: HomeStyleScheduleStageCode,
    path: "/schedule" | "/schedule/restore-auto",
    method: "POST" | "PATCH",
    body: unknown,
    signal?: AbortSignal,
  ) {
    const fingerprint = await sha256Hex(new TextEncoder().encode(stableJson({ action: "home-style-schedule-v1", id, code, path, body })));
    const workflow = await this.request<HomeStyleWorkflowView>(
      `/home-style/projects/${encodeURIComponent(id)}/workflow/stages/${encodeURIComponent(code)}${path}`,
      {
        method,
        body,
        headers: { "Idempotency-Key": `schedule-v1-${fingerprint}` },
        signal,
      },
    );
    this.invalidate("/home-style");
    return workflow;
  }

  updateHomeStyleStageSchedule(
    id: string,
    code: HomeStyleScheduleStageCode,
    input: {
      expectedVersion: number;
      scheduledOn: string | null;
      expectedConstructionScheduleVersion?: number;
    },
    signal?: AbortSignal,
  ) {
    return this.homeStyleScheduleCommand(id, code, "/schedule", "PATCH", input, signal);
  }

  restoreHomeStyleStageSchedule(
    id: string,
    code: HomeStyleScheduleStageCode,
    expectedVersion: number,
    expectedConstructionScheduleVersion?: number,
    signal?: AbortSignal,
  ) {
    return this.homeStyleScheduleCommand(id, code, "/schedule/restore-auto", "POST", {
      expectedVersion,
      ...(expectedConstructionScheduleVersion !== undefined
        ? { expectedConstructionScheduleVersion } : {}),
    }, signal);
  }

  async correctHomeStyleStageCompletion(
    id: string,
    code: HomeStyleWorkflowStageCode,
    input: { expectedVersion: number; completedOn: string; reason: string },
    signal?: AbortSignal,
  ) {
    const fingerprint = await sha256Hex(new TextEncoder().encode(stableJson({
      action: "home-style-completion-correction-v1",
      id,
      code,
      input,
    })));
    const workflow = await this.request<HomeStyleWorkflowView>(
      `/home-style/projects/${encodeURIComponent(id)}/workflow/stages/${encodeURIComponent(code)}/completion-date`,
      {
        method: "PATCH",
        body: input,
        headers: { "Idempotency-Key": `completion-correction-v1-${fingerprint}` },
        signal,
      },
    );
    this.invalidate("/home-style");
    return workflow;
  }

  async assignHomeStyleSpecialist(
    id: string,
    code: "electrical" | "photography",
    input: {
      expectedVersion: number;
      userId: string | null;
      specialistRole: "ELECTRICIAN" | "PHOTOGRAPHER" | null;
    },
    signal?: AbortSignal,
  ) {
    const fingerprint = await sha256Hex(new TextEncoder().encode(stableJson({
      action: "home-style-specialist-assignment-v1",
      id,
      code,
      input,
    })));
    const workflow = await this.request<HomeStyleWorkflowView>(
      `/home-style/projects/${encodeURIComponent(id)}/workflow/stages/${code}/assignment`,
      {
        method: "PUT",
        body: input,
        headers: { "Idempotency-Key": `assignment-v1-${fingerprint}` },
        signal,
      },
    );
    this.invalidate("/home-style");
    this.invalidate(`/home-style/projects/${encodeURIComponent(id)}/workflow`);
    return workflow;
  }

  async previewHomeStyleBalanceDate(
    id: string,
    input: { expectedVersion: number; balanceDueDate: string | null },
    signal?: AbortSignal,
  ) {
    const fingerprint = await sha256Hex(new TextEncoder().encode(stableJson({ action: "home-style-balance-date-preview-v1", id, input })));
    return this.request<HomeStyleBalanceDatePreview>(
      `/home-style/projects/${encodeURIComponent(id)}/workflow/balance-date/preview`,
      {
        method: "POST",
        body: input,
        headers: { "Idempotency-Key": `balance-preview-v1-${fingerprint}` },
        signal,
      },
    );
  }

  async applyHomeStyleBalanceDate(
    id: string,
    input: { expectedVersion: number; balanceDueDate: string | null },
    signal?: AbortSignal,
  ) {
    const fingerprint = await sha256Hex(new TextEncoder().encode(stableJson({ action: "home-style-balance-date-v1", id, input })));
    const workflow = await this.request<HomeStyleWorkflowView>(
      `/home-style/projects/${encodeURIComponent(id)}/workflow/balance-date/apply`,
      {
        method: "POST",
        body: input,
        headers: { "Idempotency-Key": `balance-date-v1-${fingerprint}` },
        signal,
      },
    );
    this.invalidate("/home-style");
    return workflow;
  }

  async enableHomeStyleWorkflow(
    id: string,
    expectedVersion: number,
    legacyCandidateReviews: import("./types").HomeStyleLegacyCandidateReview[],
    signal?: AbortSignal,
  ) {
    const fingerprint = await sha256Hex(
      new TextEncoder().encode(stableJson({
        action: "project-selmo-workflow-enable-v2",
        id,
        expectedVersion,
        legacyCandidateReviews,
      })),
    );
    return this.request<HomeStyleWorkflowView>(
      `/home-style/projects/${encodeURIComponent(id)}/workflow/enable`,
      {
        method: "POST",
        headers: { "Idempotency-Key": `workflow-v2-${fingerprint}` },
        body: { confirmed: true, legacyCandidateReviews, expectedVersion },
        signal,
      },
    );
  }

  private async homeStyleWorkflowCommand(
    id: string,
    path: string,
    body: unknown,
    signal?: AbortSignal,
  ) {
    const fingerprint = await sha256Hex(new TextEncoder().encode(stableJson({ id, path, body })));
    const headers = new Headers({ "Idempotency-Key": `workflow-v1-${fingerprint}` });
    const workflow = await this.request<HomeStyleWorkflowView>(
      `/home-style/projects/${encodeURIComponent(id)}/workflow${path}`,
      { method: "POST", body, headers, signal },
    );
    this.invalidate("/home-style");
    return workflow;
  }

  startHomeStyleStage(id: string, code: HomeStyleWorkflowStageCode, input: HomeStyleWorkflowCommand, signal?: AbortSignal) {
    return this.homeStyleWorkflowCommand(id, `/stages/${encodeURIComponent(code)}/start`, input, signal);
  }

  completeHomeStyleStage(id: string, code: HomeStyleWorkflowStageCode, input: HomeStyleStageCompleteCommand, signal?: AbortSignal) {
    return this.homeStyleWorkflowCommand(id, `/stages/${encodeURIComponent(code)}/complete`, input, signal);
  }

  reopenHomeStyleStage(id: string, code: HomeStyleWorkflowStageCode, expectedVersion: number, reason: string, signal?: AbortSignal) {
    return this.homeStyleWorkflowCommand(id, `/stages/${encodeURIComponent(code)}/reopen`, { expectedVersion, reason }, signal);
  }

  skipHomeStyleStage(id: string, code: HomeStyleWorkflowStageCode, expectedVersion: number, reason: string, signal?: AbortSignal) {
    return this.homeStyleWorkflowCommand(id, `/stages/${encodeURIComponent(code)}/skip`, { expectedVersion, reason }, signal);
  }

  satisfyHomeStyleConstructionGate(id: string, expectedVersion: number, completedOn?: string, signal?: AbortSignal) {
    return this.homeStyleWorkflowCommand(id, "/construction-gate/satisfy", {
      expectedVersion,
      source: "operator_confirmed",
      ...(completedOn ? { completedOn } : {}),
    }, signal);
  }

  completeHomeStyleWorkflow(id: string, expectedVersion: number, completedOn: string, note?: string, signal?: AbortSignal) {
    return this.homeStyleWorkflowCommand(id, "/complete", {
      expectedVersion,
      completedOn,
      confirmed: true,
      note,
    }, signal);
  }

  async homeStyleMedia(id: string, signal?: AbortSignal): Promise<HomeStyleMediaView> {
    const root = `/home-style/projects/${encodeURIComponent(id)}`;
    const [folders, assets, documents] = await Promise.all([
      this.request<{ items: HomeStyleMediaView["folders"]; uploadEnabled: boolean }>(`${root}/media-folders`, {
        signal,
        cacheTtlMs: 2_000,
      }),
      this.request<{ items: HomeStyleMediaView["assets"] }>(`${root}/media-assets`, {
        signal,
        cacheTtlMs: 2_000,
      }),
      this.request<{ items: HomeStyleMediaView["documents"] }>(`${root}/customer-documents`, {
        signal,
        cacheTtlMs: 2_000,
      }),
    ]);
    return {
      featureEnabled: folders.uploadEnabled,
      readOnly: !folders.uploadEnabled,
      folders: folders.items,
      assets: assets.items,
      documents: documents.items,
    };
  }

  homeStyleActiveResources(id: string, signal?: AbortSignal): Promise<HomeStyleActiveResourcePreview> {
    return this.request<HomeStyleActiveResourcePreview>(
      `/home-style/projects/${encodeURIComponent(id)}/active-resources`,
      { signal, cacheTtlMs: 5 * 60_000 },
    );
  }

  homeStyleActiveDesignPreviewUrl(id: string): string {
    return this.url(`/home-style/projects/${encodeURIComponent(id)}/active-resources/design-preview`);
  }

  homeStyleActivePhotoPreviewUrl(
    id: string,
    role: "before" | "construction" | "after",
    driveFileId: string,
  ): string {
    return this.url(
      `/home-style/projects/${encodeURIComponent(id)}/active-resources/photos/${role}/${encodeURIComponent(driveFileId)}`,
    );
  }

  resolveHomeStyleMediaFolders(id: string, input: {
    createMissing: boolean;
    confirmations?: Partial<Record<"before" | "construction" | "after", string>>;
  }, signal?: AbortSignal) {
    return this.request<{ items: HomeStyleMediaView["folders"] }>(
      `/home-style/projects/${encodeURIComponent(id)}/media-folders/resolve`,
      { method: "POST", body: input, signal },
    );
  }

  homeStyleExistingMediaCandidates(id: string, code: "before" | "electrical" | "photography", signal?: AbortSignal) {
    return this.request<{ items: HomeStyleExistingMediaCandidateDto[] }>(
      `/home-style/projects/${encodeURIComponent(id)}/workflow/stages/${code}/assets/candidates`,
      { signal, cacheTtlMs: 2_000 },
    );
  }

  linkExistingHomeStyleMedia(id: string, code: "before" | "electrical" | "photography", driveFileId: string, signal?: AbortSignal) {
    return sha256Hex(new TextEncoder().encode(`project-selmo-media-link-v1\n${id}\n${code}\n${driveFileId}`))
      .then((fingerprint) => this.request<HomeStyleMediaAssetDto>(
        `/home-style/projects/${encodeURIComponent(id)}/workflow/stages/${code}/assets/link`,
        {
          method: "POST",
          headers: { "Idempotency-Key": `media-link-v1-${fingerprint}` },
          body: { driveFileId },
          signal,
        },
      ));
  }

  async uploadHomeStyleStageAssets(
    id: string,
    code: "before" | "electrical" | "photography",
    files: readonly File[],
    _expectedVersion: number,
    signal?: AbortSignal,
  ) {
    const queue = [...files];
    const items: HomeStyleMediaAssetDto[] = [];
    const uploadOne = async (file: File) => {
      const body = new FormData();
      body.append("file", file, file.name);
      const idempotencyKey = await homeStyleMediaIdempotencyKey(id, code, file);
      const response = await fetch(this.url(`/home-style/projects/${encodeURIComponent(id)}/workflow/stages/${code}/assets`), {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", "Idempotency-Key": idempotencyKey },
        body,
        signal,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = typeof payload === "object" && payload && "message" in payload
          ? String(payload.message) : "사진을 업로드하지 못했습니다.";
        throw new ApiError(message, response.status, payload);
      }
      items.push(payload as HomeStyleMediaAssetDto);
    };
    const results = await Promise.allSettled(Array.from({ length: Math.min(3, queue.length) }, async () => {
      while (queue.length) {
        const file = queue.shift();
        if (file) await uploadOne(file);
      }
    }));
    const failure = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
    if (failure) throw failure.reason;
    this.invalidate("/home-style");
    return { items };
  }

  updateHomeStyleMediaPublication(id: string, assetId: string, published: boolean, signal?: AbortSignal) {
    return this.request<HomeStyleMediaAssetDto>(
      `/home-style/projects/${encodeURIComponent(id)}/media-assets/${encodeURIComponent(assetId)}/${published ? "publish" : "unpublish"}`,
      { method: "POST", headers: { "Idempotency-Key": globalThis.crypto.randomUUID() }, signal },
    );
  }

  tombstoneHomeStyleMedia(id: string, assetId: string, signal?: AbortSignal) {
    return this.request<HomeStyleMediaAssetDto>(
      `/home-style/projects/${encodeURIComponent(id)}/media-assets/${encodeURIComponent(assetId)}/tombstone`,
      { method: "POST", headers: { "Idempotency-Key": globalThis.crypto.randomUUID() }, signal },
    );
  }

  async uploadHomeStyleCustomerDocument(
    id: string,
    kind: "design" | "purchase",
    label: string,
    file: File,
    signal?: AbortSignal,
  ) {
    const body = new FormData();
    body.append("file", file, file.name);
    // A fresh operator intent may legitimately reuse the same bytes with a new
    // label or after tombstoning. Keep the key stable only for this request.
    const requestId = globalThis.crypto.randomUUID();
    const response = await fetch(this.url(
      `/home-style/projects/${encodeURIComponent(id)}/customer-documents/${kind}?label=${encodeURIComponent(label)}`,
    ), {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Idempotency-Key": `safe-document-v1-${requestId}`,
        "X-Customer-Safe-Snapshot-Confirmed": "true",
      },
      body,
      signal,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new ApiError(
      typeof payload === "object" && payload && "message" in payload ? String(payload.message) : "고객 공개 자료를 업로드하지 못했습니다.",
      response.status,
      payload,
    );
    this.invalidate("/home-style");
    return payload as HomeStyleMediaView["documents"][number];
  }

  updateHomeStyleCustomerDocumentPublication(id: string, assetId: string, published: boolean, signal?: AbortSignal) {
    return this.request<HomeStyleMediaView["documents"][number]>(
      `/home-style/projects/${encodeURIComponent(id)}/customer-documents/${encodeURIComponent(assetId)}/${published ? "publish" : "unpublish"}`,
      { method: "POST", headers: { "Idempotency-Key": globalThis.crypto.randomUUID() }, signal },
    );
  }

  tombstoneHomeStyleCustomerDocument(id: string, assetId: string, signal?: AbortSignal) {
    return this.request<HomeStyleMediaView["documents"][number]>(
      `/home-style/projects/${encodeURIComponent(id)}/customer-documents/${encodeURIComponent(assetId)}/tombstone`,
      { method: "POST", headers: { "Idempotency-Key": globalThis.crypto.randomUUID() }, signal },
    );
  }

  guestSession(signal?: AbortSignal) {
    return this.request<GuestHomeStyleSession>("/guest/session", { signal, cacheTtlMs: 2_000 });
  }

  guestConfig(signal?: AbortSignal) {
    return this.request<GuestPortalRuntimeConfig>("/guest/config", { signal, cacheTtlMs: 60_000 });
  }

  guestProjects(signal?: AbortSignal) {
    return this.request<{ items: GuestHomeStyleProjectDto[] }>("/guest/projects", { signal, cacheTtlMs: 2_000 });
  }

  guestProject(publicProjectId: string, signal?: AbortSignal) {
    return this.request<GuestHomeStyleProjectDto>(`/guest/projects/${encodeURIComponent(publicProjectId)}`, {
      signal,
      cacheTtlMs: 2_000,
    });
  }

  consumeGuestInvite(token: string, signal?: AbortSignal) {
    return this.request<GuestInviteConsumption>("/guest/invites/consume", {
      method: "POST",
      body: { token },
      signal,
    });
  }

  guestPasswordLogin(email: string, password: string, signal?: AbortSignal) {
    return this.request<GuestHomeStyleSession>("/guest/sessions/password", {
      method: "POST",
      body: { email, password },
      signal,
    });
  }

  guestGoogleLogin(credential: string, signal?: AbortSignal) {
    return this.request<GuestHomeStyleSession>("/guest/sessions/google", {
      method: "POST",
      body: { credential },
      signal,
    });
  }

  guestOnboarding(input:
    | { method: "password"; onboardingTicket: string; password: string }
    | { method: "google"; onboardingTicket: string; credential: string }
    | { method: "both"; onboardingTicket: string; password: string; credential: string },
  signal?: AbortSignal) {
    return this.request<GuestHomeStyleSession>("/guest/onboarding", {
      method: "POST",
      body: input,
      signal,
    });
  }

  async guestLogout(signal?: AbortSignal) {
    await this.request<void>("/guest/session", { method: "DELETE", signal });
    this.invalidate("/guest");
  }

  submitGuestIntake(publicProjectId: string, input: GuestIntakeSubmissionInput, signal?: AbortSignal) {
    return this.request<{ id: string; status: "pending_review" }>(`/guest/projects/${encodeURIComponent(publicProjectId)}/intake-submissions`, {
      method: "POST",
      body: input,
      signal,
    });
  }

  homeStyleGuestInvites(projectId: string, signal?: AbortSignal) {
    return this.request<{ items: HomeStyleGuestInviteDto[] }>(
      `/home-style/guest-admin/projects/${encodeURIComponent(projectId)}/invites`,
      { signal, cacheTtlMs: 2_000 },
    );
  }

  async createHomeStyleGuestInvite(projectId: string, recipientEmail: string, signal?: AbortSignal) {
    const invite = await this.request<HomeStyleGuestInviteDto>(
      `/home-style/guest-admin/projects/${encodeURIComponent(projectId)}/invites`,
      { method: "POST", body: { recipientEmail }, signal },
    );
    this.invalidate("/home-style/guest-admin");
    return invite;
  }

  async revokeHomeStyleGuestInvite(projectId: string, inviteId: string, signal?: AbortSignal) {
    await this.request<void>(
      `/home-style/guest-admin/projects/${encodeURIComponent(projectId)}/invites/${encodeURIComponent(inviteId)}/revoke`,
      { method: "POST", signal },
    );
    this.invalidate("/home-style/guest-admin");
  }

  homeStyleGuestGrants(projectId: string, signal?: AbortSignal) {
    return this.request<{ items: HomeStyleGuestGrantDto[] }>(
      `/home-style/guest-admin/projects/${encodeURIComponent(projectId)}/grants`,
      { signal, cacheTtlMs: 2_000 },
    );
  }

  async revokeHomeStyleGuestGrant(projectId: string, grantId: string, signal?: AbortSignal) {
    await this.request<void>(
      `/home-style/guest-admin/projects/${encodeURIComponent(projectId)}/grants/${encodeURIComponent(grantId)}/revoke`,
      { method: "POST", signal },
    );
    this.invalidate("/home-style/guest-admin");
  }

  homeStyleGuestPreview(projectId: string, signal?: AbortSignal) {
    return this.request<GuestHomeStyleProjectDto>(
      `/home-style/guest-admin/projects/${encodeURIComponent(projectId)}/preview`,
      { signal, cacheTtlMs: 2_000 },
    );
  }

  homeStyleGuestStagePublications(projectId: string, signal?: AbortSignal) {
    return this.request<{ items: HomeStyleGuestStagePublicationDto[] }>(
      `/home-style/guest-admin/projects/${encodeURIComponent(projectId)}/stage-publications`,
      { signal,cacheTtlMs: 2_000 },
    );
  }

  async setHomeStyleGuestStagePublication(
    projectId: string,
    stageCode: HomeStyleGuestStagePublicationDto["stageCode"],
    input: { published: boolean; expectedVersion: number },
    signal?: AbortSignal,
  ) {
    const fingerprint = await sha256Hex(new TextEncoder().encode(stableJson({
      action: "home-style-guest-stage-publication-v1",projectId,stageCode,input,
    })));
    const result = await this.request<HomeStyleGuestStagePublicationDto>(
      `/home-style/guest-admin/projects/${encodeURIComponent(projectId)}/stage-publications/${encodeURIComponent(stageCode)}`,
      {
        method: "PUT",body: input,
        headers: { "Idempotency-Key": `guest-stage-v1-${fingerprint}` },signal,
      },
    );
    this.invalidate("/home-style/guest-admin");
    return result;
  }

  homeStyleGuestIntakeSubmissions(status?: HomeStyleGuestIntakeSubmissionDto["status"], signal?: AbortSignal) {
    const query = status ? `?status=${encodeURIComponent(status)}` : "";
    return this.request<{ items: HomeStyleGuestIntakeSubmissionDto[] }>(
      `/home-style/guest-admin/intake-submissions${query}`,
      { signal, cacheTtlMs: 2_000 },
    );
  }

  async reviewHomeStyleGuestIntake(
    submissionId: string,
    expectedVersion: number,
    decisions: Partial<Record<GuestIntakeField, { decision: "approved" | "rejected"; note: string | null }>>,
    signal?: AbortSignal,
  ) {
    const submission = await this.request<HomeStyleGuestIntakeSubmissionDto>(
      `/home-style/guest-admin/intake-submissions/${encodeURIComponent(submissionId)}/review`,
      { method: "POST", body: { expectedVersion, decisions }, signal },
    );
    this.invalidate("/home-style/guest-admin");
    this.invalidate("/home-style");
    return submission;
  }

  homeStyleEditSession(signal?: AbortSignal) {
    return this.request<{ session: HomeStyleEditSession | null }>("/home-style/sheet-edit-session", { signal });
  }

  startHomeStyleEditSession(ttlMinutes = 30, signal?: AbortSignal) {
    return this.request<{ session: HomeStyleEditSession }>("/home-style/sheet-edit-session/start", {
      method: "POST", body: { ttlMinutes }, signal,
    });
  }

  commitHomeStyleEditSession(id: string, signal?: AbortSignal) {
    return this.request<{ changedProjects: number; validation: Record<string, unknown> }>(
      `/home-style/sheet-edit-session/${encodeURIComponent(id)}/commit`, { method: "POST", signal },
    );
  }

  cancelHomeStyleEditSession(id: string, signal?: AbortSignal) {
    return this.request<void>(`/home-style/sheet-edit-session/${encodeURIComponent(id)}/cancel`, {
      method: "POST", signal,
    });
  }

  deviceDeliveryMonitoring(signal?: AbortSignal) {
    return this.request<DeviceDeliveryMonitoringView>("/admin/device-delivery-monitoring", {
      signal,
      cacheTtlMs: 15_000,
    });
  }

  async acknowledgeCustomerQuoteSms(quotes: Array<{ quoteId: string; approvedAt: string }>) {
    const result = await this.request<DeviceDeliveryMonitoringView>("/admin/device-delivery-monitoring/customer-quotes/acknowledge", {
      method: "POST", body: { quotes },
    });
    this.invalidate();
    return result;
  }

  async acknowledgeDeviceDelivery(channel: "sms" | "kakao", incidentId: string) {
    const result = await this.request<DeviceDeliveryMonitoringView>(`/admin/device-delivery-monitoring/channels/${channel}/acknowledge`, {
      method: "POST", body: { incidentId },
    });
    this.invalidate();
    return result;
  }

  authSession(signal?: AbortSignal) {
    return this.request<AuthSession>("/auth/session", { signal });
  }

  async googleLogin(credential: string, signal?: AbortSignal) {
    const result = await this.request<Partial<AuthSession>>("/auth/google", {
      method: "POST",
      body: { credential },
      signal,
    });
    this.invalidate();
    return result;
  }

  async nameLogin(name: string, accessCode?: string, signal?: AbortSignal) {
    const result = await this.request<Partial<AuthSession>>("/auth/name", {
      method: "POST",
      body: { name, ...(accessCode ? { accessCode } : {}) },
      signal,
    });
    this.invalidate();
    return result;
  }

  async logout(signal?: AbortSignal) {
    await this.request<unknown>("/auth/logout", { method: "POST", signal });
    this.invalidate();
  }

  calendarConnection(signal?: AbortSignal) {
    return this.request<CalendarConnectionStatus>("/calendar/connection", {
      signal,
      cacheTtlMs: 2_000,
    });
  }

  async connectCalendar(accessToken: string, signal?: AbortSignal) {
    const result = await this.request<CalendarConnectionStatus>("/calendar/connection", {
      method: "POST",
      body: { accessToken },
      signal,
    });
    this.invalidate("/calendar/connection");
    return result;
  }

  async disconnectCalendar(accessToken: string, signal?: AbortSignal) {
    const result = await this.request<CalendarConnectionStatus>("/calendar/connection", {
      method: "DELETE",
      body: { accessToken },
      signal,
    });
    this.invalidate("/calendar/connection");
    return result;
  }

  adminCalendarStatus(signal?: AbortSignal) {
    return this.request<CalendarAdminStatus>("/admin/calendar/status", {
      signal,
      cacheTtlMs: 5_000,
    });
  }

  syncStatus(signal?: AbortSignal) {
    return this.request<SyncStatus>("/sync/status", { signal, cacheTtlMs: 5_000 });
  }

  masterSummary(signal?: AbortSignal) {
    return this.request<MasterSummary>("/masters/summary", { signal, cacheTtlMs: 60_000 });
  }

  projects(
    query: { search?: string; status?: ProjectStatus | string; view?: ProjectView; managerId?: string; cursor?: string; limit?: number },
    signal?: AbortSignal,
  ) {
    const path = this.query("/projects", {
      search: query.search,
      status: query.status && query.status !== "ALL" ? query.status : undefined,
      view: query.view,
      managerId: query.managerId,
      cursor: query.cursor,
      limit: query.limit ?? 20,
    });
    return this.request<ProjectPage>(path, {
      signal,
      cacheTtlMs: 15_000,
    });
  }

  async project(id: string, signal?: AbortSignal) {
    const project = await this.request<ProjectDetail>(`/projects/${encodeURIComponent(id)}`, {
      signal,
      cacheTtlMs: 20_000,
    });
    return normalizeProject(project);
  }

  async constructionPaymentCommand(projectId: string, command: 'policy' | 'manual-confirm' | 'start', input: { quoteId: string; plan?: 'AUTO' | '40-40-20'; milestone?: string }, signal?: AbortSignal) {
    const result = await this.request<{ constructionPayment: ConstructionPaymentState }>(
      `/projects/${encodeURIComponent(projectId)}/construction-payment/${command}`,
      { method: 'POST', body: input, signal },
    );
    this.invalidate('/projects');
    this.invalidate('/dashboard');
    this.invalidate('/bank-transactions');
    return result.constructionPayment;
  }

  prefetchProject(id: string) {
    void this.project(id).catch(() => undefined);
  }

  async createProject(input: CreateProjectInput, signal?: AbortSignal) {
    const created = await this.request<ProjectDetail>("/projects", {
      method: "POST",
      body: input,
      signal,
    });
    this.invalidate("/projects");
    this.invalidate("/masters/summary");
    this.invalidate("/dashboard");
    return normalizeProject(created);
  }

  async updateProject(id: string, input: UpdateProjectInput, signal?: AbortSignal) {
    const updated = await this.request<ProjectDetail>(`/projects/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: input,
      signal,
    });
    this.invalidate("/projects");
    this.invalidate("/dashboard");
    return normalizeProject(updated);
  }

  async deleteProject(id: string, signal?: AbortSignal) {
    const result = await this.request<{ id: string; deletedAt: string; purgeAfter: string }>(
      `/projects/${encodeURIComponent(id)}`,
      { method: "DELETE", signal },
    );
    this.invalidate("/projects");
    this.invalidate("/admin/project-trash");
    this.invalidate("/masters/summary");
    this.invalidate("/dashboard");
    return result;
  }

  projectTrash(
    query: { cursor?: string; limit?: number } = {},
    signal?: AbortSignal,
  ) {
    const path = this.query("/admin/project-trash", {
      cursor: query.cursor,
      limit: query.limit ?? 50,
    });
    return this.request<ProjectTrashPage>(path, { signal, cacheTtlMs: 10_000 });
  }

  async restoreProject(id: string, signal?: AbortSignal) {
    const restored = await this.request<ProjectDetail>(
      `/admin/project-trash/${encodeURIComponent(id)}/restore`,
      { method: "POST", signal },
    );
    this.invalidate("/projects");
    this.invalidate("/admin/project-trash");
    this.invalidate("/masters/summary");
    this.invalidate("/dashboard");
    return normalizeProject(restored);
  }

  async purgeProject(id: string, signal?: AbortSignal) {
    const result = await this.request<{ id: string; purgedAt: string }>(
      `/admin/project-trash/${encodeURIComponent(id)}`,
      { method: "DELETE", signal },
    );
    this.invalidate("/admin/project-trash");
    return result;
  }

  async masters(signal?: AbortSignal) {
    const value = await this.request<MasterData>("/masters", { signal, cacheTtlMs: 60_000 });
    return {
      ...value,
      vendors: (value.vendors ?? []).map((vendor) => ({
        ...vendor,
        bankName: vendor.bankName ?? (vendor as typeof vendor & { bank?: string }).bank,
      })),
    };
  }

  async createMasterVendor(
    input: Pick<VendorOption, "name" | "tradeIds" | "bankName" | "accountNumber" | "accountHolder" | "phone" | "evidenceType" | "note" | "taxInvoiceInfo" | "withholdingInfo" | "settlementDefaultMode">,
    signal?: AbortSignal,
  ) {
    const vendor = await this.request<VendorOption>("/masters/vendors", {
      method: "POST",
      body: {
        name: input.name,
        tradeIds: input.tradeIds,
        bank: input.bankName,
        accountNumber: input.accountNumber,
        accountHolder: input.accountHolder,
        phone: input.phone,
        evidenceType: input.evidenceType,
        note: input.note,
        taxInvoiceInfo: input.taxInvoiceInfo,
        withholdingInfo: input.withholdingInfo,
        settlementDefaultMode: input.settlementDefaultMode,
      },
      signal,
    });
    this.invalidate("/masters");
    return vendor;
  }

  async createTrade(input: { name: string; sortOrder?: number }, signal?: AbortSignal) {
    const trade = await this.request<TradeOption>("/masters/trades", { method: "POST", body: input, signal });
    this.invalidate("/masters");
    return trade;
  }

  async updateTrade(id: string, input: { name?: string; sortOrder?: number; status?: "active" | "inactive" }, signal?: AbortSignal) {
    const trade = await this.request<TradeOption>(`/masters/trades/${encodeURIComponent(id)}`, { method: "PATCH", body: input, signal });
    this.invalidate("/masters");
    return trade;
  }

  async vendorAccountLinks(id: string, signal?: AbortSignal) {
    const result = await this.request<{ items: VendorAccountLink[] }>(
      `/masters/vendors/${encodeURIComponent(id)}/account-links`,
      { signal },
    );
    return result.items ?? [];
  }

  async vendorAccountHistory(id: string, signal?: AbortSignal) {
    const result = await this.request<{ items: VendorAccountHistoryItem[] }>(
      `/masters/vendors/${encodeURIComponent(id)}/account-history`,
      { signal },
    );
    return result.items ?? [];
  }

  async updateVendor(id: string, input: Partial<Pick<VendorOption, "name" | "tradeIds" | "bankName" | "accountNumber" | "accountHolder" | "phone" | "evidenceType" | "note" | "taxInvoiceInfo" | "withholdingInfo" | "settlementDefaultMode">> & { status?: "active" | "inactive"; paymentRequestIds?: string[]; reviewedPaymentRequestIds?: string[] }, signal?: AbortSignal) {
    const vendor = await this.request<VendorOption>(`/masters/vendors/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: { ...input, bank: input.bankName },
      signal,
    });
    this.invalidate("/masters");
    return vendor;
  }

  async adminManagers(signal?: AbortSignal) {
    const value = await this.request<{ items?: ManagerOption[] }>("/admin/managers", { signal, cacheTtlMs: 30_000 });
    return value.items ?? [];
  }

  async compensationRules(signal?: AbortSignal) {
    const value = await this.request<{ items?: ManagerCompensationRule[] }>(
      "/admin/compensation-rules",
      { signal, cacheTtlMs: 30_000 },
    );
    return value.items ?? [];
  }

  compensationRuleCurrent(signal?: AbortSignal) {
    return this.request<ManagerCompensationRule>(
      "/compensation-rules/current",
      { signal, cacheTtlMs: 30_000 },
    );
  }

  async createCompensationRule(
    input: {
      effectiveFrom: string;
      customerManagerRate: number;
      constructionManagerRate: number;
      defaultQuoteMarginRate: number;
      selmoProfitRate: number;
    },
    signal?: AbortSignal,
  ) {
    const rule = await this.request<ManagerCompensationRule>("/admin/compensation-rules", {
      method: "POST",
      body: input,
      signal,
    });
    this.invalidate("/admin/compensation-rules");
    return rule;
  }

  companyQuoteProfile(signal?: AbortSignal) {
    return this.request<CompanyQuoteProfile>("/admin/company-quote-profile", { signal, cacheTtlMs: 30_000 });
  }

  async updateCompanyQuoteProfile(input: Omit<CompanyQuoteProfile, "updatedAt">, signal?: AbortSignal) {
    const profile = await this.request<CompanyQuoteProfile>("/admin/company-quote-profile", {
      method: "PATCH",
      body: input,
      signal,
    });
    this.invalidate("/admin/company-quote-profile");
    return profile;
  }

  async createManager(
    input: { name: string; role: string; roleCategory: NonNullable<ManagerOption["roleCategory"]>; displayOrder: number; phone?: string; email?: string; bank?: string; accountNumber?: string },
    signal?: AbortSignal,
  ) {
    const manager = await this.request<ManagerOption>("/admin/managers", { method: "POST", body: input, signal });
    this.invalidate("/admin/managers");
    this.invalidate("/masters");
    this.invalidate("/dashboard");
    return manager;
  }

  async updateManager(
    id: string,
    input: { name?: string; role?: string; roleCategory?: NonNullable<ManagerOption["roleCategory"]>; displayOrder?: number; phone?: string | null; email?: string | null; bank?: string | null; accountNumber?: string | null; status?: "active" | "inactive" },
    signal?: AbortSignal,
  ) {
    const manager = await this.request<ManagerOption>(`/admin/managers/${encodeURIComponent(id)}`, { method: "PATCH", body: input, signal });
    this.invalidate("/admin/managers");
    this.invalidate("/masters");
    this.invalidate("/dashboard");
    return manager;
  }

  async users(signal?: AbortSignal) {
    const value = await this.request<{ items?: AppUser[] } | AppUser[]>("/users", { signal, cacheTtlMs: 15_000 });
    return this.items(value);
  }

  async createUser(input: CreateAppUserInput, signal?: AbortSignal) {
    const user = await this.request<AppUser>("/users", { method: "POST", body: input, signal });
    this.invalidate("/users");
    return user;
  }

  async updateUser(id: string, input: UpdateAppUserInput, signal?: AbortSignal) {
    const user = await this.request<AppUser>(`/users/${encodeURIComponent(id)}`, { method: "PATCH", body: input, signal });
    this.invalidate("/users");
    return user;
  }

  syncJobs(query: { status?: string; cursor?: string; limit?: number } = {}, signal?: AbortSignal) {
    const path = this.query("/sync/jobs", { ...query, limit: query.limit ?? 30 });
    return this.request<SyncJobPage>(path, { signal, cacheTtlMs: 10_000 });
  }

  async retrySyncJob(id: string, signal?: AbortSignal) {
    const result = await this.request<{ id: string; status: string }>(`/sync/jobs/${encodeURIComponent(id)}/retry`, { method: "POST", signal });
    this.invalidate("/sync");
    return result;
  }

  async monthClosings(signal?: AbortSignal) {
    const value = await this.request<{ items?: MonthClosing[] } | MonthClosing[]>("/month-closings", { signal, cacheTtlMs: 30_000 });
    return this.items(value);
  }

  async reopenMonth(month: string, signal?: AbortSignal) {
    const value = await this.request<MonthClosing>(`/month-closings/${encodeURIComponent(month)}/reopen`, { method: "POST", signal });
    this.invalidate("/month-closings");
    this.invalidate("/dashboard");
    return value;
  }

  async projectQuotes(projectId: string, signal?: AbortSignal) {
    const value = await this.request<RawQuote[] | { items?: RawQuote[] }>(
      `/projects/${encodeURIComponent(projectId)}/quotes`,
      { signal, cacheTtlMs: 15_000 },
    );
    return this.items(value).map(normalizeQuote);
  }

  async quote(quoteId: string, signal?: AbortSignal) {
    const value = await this.request<RawQuote>(
      `/quotes/${encodeURIComponent(quoteId)}`,
      { signal },
    );
    return normalizeQuote(value);
  }

  quoteApprovals(signal?: AbortSignal) {
    return this.request<QuoteApprovalQueue>("/admin/quote-approvals", {
      signal,
      cacheTtlMs: 15_000,
    });
  }

  completedQuoteApprovals(limit = 50, cursor?: string, signal?: AbortSignal) {
    return this.request<CompletedQuoteApprovalPage>(
      this.query("/admin/quote-approvals/completed", { limit, cursor }),
      { signal, cacheTtlMs: 15_000 },
    );
  }

  async createQuote(projectId: string, input: CreateQuoteInput, signal?: AbortSignal) {
    const quote = await this.request<RawQuote>(`/projects/${encodeURIComponent(projectId)}/quotes`, {
      method: "POST",
      body: input,
      signal,
    });
    this.invalidate(`/projects/${encodeURIComponent(projectId)}`);
    this.invalidate(`/projects/${encodeURIComponent(projectId)}/quotes`);
    this.invalidate("/dashboard");
    return normalizeQuote(quote);
  }

  async discardQuote(quoteId: string, reason?: string, signal?: AbortSignal) {
    const result = await this.request<{ id: string; status: "deleted" }>(`/quotes/${encodeURIComponent(quoteId)}`, {
      method: "DELETE",
      body: { reason },
      signal,
    });
    this.invalidate("/projects");
    this.invalidate(`/quotes/${encodeURIComponent(quoteId)}`);
    this.invalidate("/dashboard");
    return result;
  }

  async deleteQuoteItem(quoteId: string, itemId: string, signal?: AbortSignal) {
    const quote = await this.request<RawQuote>(
      `/quotes/${encodeURIComponent(quoteId)}/items/${encodeURIComponent(itemId)}`,
      { method: "DELETE", signal },
    );
    this.invalidate("/projects");
    this.invalidate(`/quotes/${encodeURIComponent(quoteId)}`);
    this.invalidate("/dashboard");
    return normalizeQuote(quote);
  }

  async registerOneTimeVendor(id: string, source: { quoteItemId?: string; asExpenseLineId?: string }) {
    await this.request(`/masters/vendors/${encodeURIComponent(id)}/register`, { method: "POST", body: source });
    this.invalidate();
  }

  async saveQuoteItem(quoteId: string, input: SaveQuoteItemInput, signal?: AbortSignal) {
    const path = input.id
      ? `/quotes/${encodeURIComponent(quoteId)}/items/${encodeURIComponent(input.id)}`
      : `/quotes/${encodeURIComponent(quoteId)}/items`;
    const item = await this.request<RawQuote>(path, {
      method: input.id ? "PATCH" : "POST",
      body: {
        tradeId: input.tradeId,
        constructionManagerId: input.constructionManagerId,
        detail: input.detail,
        scheduledDate: input.scheduledDate,
        scheduledEndDate: input.scheduledEndDate,
        vendorId: input.vendorId,
        newVendor: input.newVendor ? {
          name: input.newVendor.name, registrationScope: input.newVendor.registrationScope, settlementDefaultMode: input.newVendor.settlementDefaultMode,
          bank: input.newVendor.bankName,
          accountNumber: input.newVendor.accountNumber,
          accountHolder: input.newVendor.accountHolder,
          phone: input.newVendor.phone,
          settlementType: input.settlementType === "legacy" ? undefined : input.settlementType,
          settlementInfo: input.settlementInfo,
        } : undefined,
        settlementType: input.settlementType,
        settlementSelectionSource: input.settlementSelectionSource,
        amountInputType: input.amountInputType,
        contractAmount: input.contractAmount,
        settlementInfo: input.settlementInfo,
        updateVendorSettlementInfo: input.updateVendorSettlementInfo,
        calculationMode: input.calculationMode,
        changeType: input.changeType,
        vendorCost: input.vendorCost,
        marginBurden: input.marginBurden,
        marginRate: input.marginRate,
        customerSupplyAmount: input.customerSupplyAmount,
        customerAmount: input.customerAmount,
      },
      signal,
    });
    this.invalidate("/projects");
    this.invalidate(`/quotes/${encodeURIComponent(quoteId)}`);
    this.invalidate("/dashboard");
    return normalizeQuote(item);
  }

  async updateQuoteItemSchedule(
    quoteId: string,
    itemId: string,
    input: UpdateQuoteItemScheduleInput,
    signal?: AbortSignal,
  ) {
    const quote = await this.request<RawQuote>(
      `/quotes/${encodeURIComponent(quoteId)}/items/${encodeURIComponent(itemId)}/schedule`,
      { method: "PATCH", body: input, signal },
    );
    this.invalidate("/projects");
    this.invalidate(`/quotes/${encodeURIComponent(quoteId)}`);
    this.invalidate("/dashboard");
    return normalizeQuote(quote);
  }

  async requestQuoteApproval(quoteId: string, signal?: AbortSignal) {
    const quote = await this.request<RawQuote>(`/quotes/${encodeURIComponent(quoteId)}/approval-request`, {
      method: "POST",
      signal,
    });
    this.invalidate("/projects");
    this.invalidate("/admin/quote-approvals");
    this.invalidate("/dashboard");
    return normalizeQuote(quote);
  }

  async updateQuoteCustomerGroups(quoteId: string, groups: QuoteCustomerGroup[], expectedToken: string) {
    const quote = await this.request<RawQuote>(`/quotes/${encodeURIComponent(quoteId)}/customer-groups`, {
      method: "PUT", body: { groups, expectedToken },
    });
    this.invalidate("/projects"); this.invalidate("/quotes"); this.invalidate("/admin/quote-approvals");
    return normalizeQuote(quote);
  }

  async quotePreviewHtml(quoteId: string, signal?: AbortSignal) {
    const result = await this.request<{ html: string }>(
      `/quotes/${encodeURIComponent(quoteId)}/preview`,
      { signal },
    );
    return result.html;
  }

  async rejectQuoteApproval(quoteId: string, reason: string, signal?: AbortSignal) {
    try {
      const quote = await this.request<RawQuote>(`/quotes/${encodeURIComponent(quoteId)}/reject`, {
        method: "POST",
        body: { reason },
        signal,
      });
      return normalizeQuote(quote);
    } finally {
      this.invalidateRejectedApprovalCaches(quoteId);
    }
  }

  invalidateRejectedApprovalCaches(quoteId: string) {
    this.invalidate("/projects");
    this.invalidate(`/quotes/${encodeURIComponent(quoteId)}`);
    this.invalidate("/admin/quote-approvals");
    this.invalidate("/dashboard");
  }

  invalidateFinalApprovalCaches(quoteId: string) {
    this.invalidate("/projects");
    this.invalidate(`/quotes/${encodeURIComponent(quoteId)}`);
    this.invalidate("/admin/quote-approvals");
    this.invalidate("/vendor-payment-obligations");
    this.invalidate("/customer-receipts");
    this.invalidate("/customer-refund-claims");
    this.invalidate("/admin/customer-refund-approvals");
    this.invalidate("/dashboard");
  }

  async reconcileQuoteAfterApprovalError(quoteId: string, signal?: AbortSignal) {
    const quote = await this.quote(quoteId, signal);
    if (quote.status === "CONFIRMED") this.invalidateFinalApprovalCaches(quoteId);
    if (quote.status === "REJECTED") this.invalidateRejectedApprovalCaches(quoteId);
    return quote;
  }

  async finalApproveQuote(quoteId: string, signal?: AbortSignal, expectedGroupingToken?: string) {
    try {
      const quote = await this.request<RawQuote>(`/quotes/${encodeURIComponent(quoteId)}/final-approve`, {
        method: "POST",
        body: { expectedGroupingToken },
        signal,
      });
      return normalizeQuote(quote);
    } finally {
      this.invalidateFinalApprovalCaches(quoteId);
    }
  }

  confirmQuote(quoteId: string, signal?: AbortSignal) {
    return this.finalApproveQuote(quoteId, signal);
  }

  async retryQuotePdf(quoteId: string, replaceExisting = false, signal?: AbortSignal) {
    const quote = await this.request<RawQuote>(`/quotes/${encodeURIComponent(quoteId)}/pdf`, {
      method: "POST",
      body: { replaceExisting },
      signal,
    });
    this.invalidate("/projects");
    this.invalidate(`/quotes/${encodeURIComponent(quoteId)}`);
    return normalizeQuote(quote);
  }

  async reissueQuotePdfLink(quoteId: string, signal?: AbortSignal) {
    const result = await this.request<{ url: string; status: string; expiresAt: string | null }>(
      `/quotes/${encodeURIComponent(quoteId)}/link/reissue`,
      {
        method: "POST",
        body: {},
        signal,
      },
    );
    this.invalidate("/projects");
    this.invalidate(`/quotes/${encodeURIComponent(quoteId)}`);
    return {
      ...result,
      status: normalizeStatus(result.status),
    } as QuotePdfLinkResult;
  }

  async blockQuotePdfLink(quoteId: string, signal?: AbortSignal) {
    const result = await this.request<{ status: string }>(
      `/quotes/${encodeURIComponent(quoteId)}/link/block`,
      { method: "POST", signal },
    );
    this.invalidate("/projects");
    this.invalidate(`/quotes/${encodeURIComponent(quoteId)}`);
    return result;
  }

  parseDepositNotification(rawText: string, signal?: AbortSignal) {
    return this.request<{
      parsed?: { occurredAt?: string | null; depositor?: string | null; amount?: number | null };
      fingerprint?: string;
      missingFields?: string[];
      notification?: { id?: string; occurredAt?: string; depositor?: string; amount?: number } | null;
    }>("/deposit-notifications/parse", {
      method: "POST",
      body: { raw: rawText },
      signal,
    }).then((result): ParsedDepositNotification => ({
      rawText,
      notificationId: result.notification?.id,
      depositedAt: result.parsed?.occurredAt ?? result.notification?.occurredAt ?? undefined,
      payer: result.parsed?.depositor ?? result.notification?.depositor ?? undefined,
      amount: result.parsed?.amount ?? result.notification?.amount ?? undefined,
      bankName: "우리은행",
      fingerprint: result.fingerprint,
      warnings: result.missingFields?.map((field) => `${field === "occurredAt" ? "입금일시" : field === "amount" ? "금액" : field}을 직접 확인해주세요.`),
    }));
  }

  unmatchedDepositNotifications(signal?: AbortSignal) {
    return this.request<{ items: UnmatchedDepositNotification[] }>("/deposit-notifications", {
      signal,
      cacheTtlMs: 10_000,
    });
  }

  async dismissDepositNotification(id: string, reason: string, signal?: AbortSignal) {
    const result = await this.request<UnmatchedDepositNotification & { status: string }>(
      `/deposit-notifications/${encodeURIComponent(id)}/dismiss`,
      { method: "POST", body: { reason }, signal },
    );
    this.invalidate("/deposit-notifications");
    this.invalidate("/dashboard");
    return result;
  }

  async receipts(
    query: { projectId?: string; month?: string; cursor?: string; limit?: number } = {},
    signal?: AbortSignal,
  ) {
    const path = this.query("/customer-receipts", { ...query, limit: query.limit ?? 30 });
    const value = await this.request<CustomerReceiptPage | CustomerReceipt[]>(path, { signal, cacheTtlMs: 10_000 });
    const page = Array.isArray(value) ? { items: value } : value;
    return {
      ...page,
      items: (page.items ?? []).map((receipt) => {
        const raw = receipt as CustomerReceipt & { depositor?: string; receiptState?: string; derivedStatus?: string };
        const ledgerStatus = normalizeStatus(receipt.status);
        const displayStatus = ledgerStatus === "REVERSED" || ledgerStatus === "CORRECTION"
          ? ledgerStatus
          : normalizeStatus(raw.receiptState || raw.derivedStatus || receipt.status);
        return { ...receipt, payer: receipt.payer ?? raw.depositor, status: displayStatus };
      }),
    };
  }

  async createReceipt(input: CreateCustomerReceiptInput, signal?: AbortSignal) {
    const receipt = await this.financialRequest<CustomerReceipt>("/customer-receipts", {
      method: "POST",
      body: {
        projectId: input.projectId,
        notificationId: input.notificationId,
        depositedAt: input.depositedAt,
        depositor: input.payer,
        amount: input.amount,
        rawText: input.rawText,
        fingerprint: input.fingerprint,
        allocations: input.allocations,
      },
      signal,
    });
    this.invalidate("/customer-receipts");
    this.invalidate("/deposit-notifications");
    this.invalidate("/projects");
    this.invalidate("/dashboard");
    return { ...receipt, status: normalizeStatus(receipt.status) };
  }

  async reverseReceipt(id: string, reason: string, signal?: AbortSignal) {
    const receipt = await this.financialRequest<CustomerReceipt>(`/customer-receipts/${encodeURIComponent(id)}/reverse`, {
      method: "POST",
      body: { reason },
      signal,
    });
    this.invalidate("/customer-receipts");
    this.invalidate("/deposit-notifications");
    this.invalidate("/customer-refund-claims");
    this.invalidate("/admin/customer-refund-approvals");
    this.invalidate("/projects");
    this.invalidate("/dashboard");
    return { ...receipt, status: normalizeStatus(receipt.status) };
  }

  async customerRefundClaims(projectId?: string, signal?: AbortSignal) {
    const path = this.query("/customer-refund-claims", { projectId });
    const value = await this.request<{ items?: CustomerRefundClaim[] } | CustomerRefundClaim[]>(
      path,
      { signal, cacheTtlMs: 10_000 },
    );
    return this.items(value).map((claim) => ({
      ...claim,
      status: normalizeStatus(claim.status),
      payments: (claim.payments ?? []).map((payment) => ({
        ...payment,
        status: normalizeStatus(payment.status),
      })),
    }));
  }

  async customerRefundApprovals(signal?: AbortSignal) {
    const queue = await this.request<CustomerRefundApprovalQueue>("/admin/customer-refund-approvals", {
      signal,
      cacheTtlMs: 10_000,
    });
    return {
      ...queue,
      items: (queue.items ?? []).map((claim) => ({
        ...claim,
        status: normalizeStatus(claim.status),
        payments: claim.payments ?? [],
      })),
    };
  }

  async requestCustomerRefund(
    id: string,
    input: { reason: string; accountHolder: string; bank: string; accountNumber: string },
    signal?: AbortSignal,
  ) {
    const claim = await this.request<CustomerRefundClaim>(
      `/customer-refund-claims/${encodeURIComponent(id)}/request`,
      { method: "POST", body: input, signal },
    );
    this.invalidateCustomerRefund(id, claim.projectId);
    return { ...claim, status: normalizeStatus(claim.status) };
  }

  async approveCustomerRefund(id: string, signal?: AbortSignal) {
    const claim = await this.request<CustomerRefundClaim>(
      `/customer-refund-claims/${encodeURIComponent(id)}/approve`,
      { method: "POST", signal },
    );
    this.invalidateCustomerRefund(id, claim.projectId);
    return { ...claim, status: normalizeStatus(claim.status) };
  }

  async rejectCustomerRefund(id: string, reason: string, signal?: AbortSignal) {
    const claim = await this.request<CustomerRefundClaim>(
      `/customer-refund-claims/${encodeURIComponent(id)}/reject`,
      { method: "POST", body: { reason }, signal },
    );
    this.invalidateCustomerRefund(id, claim.projectId);
    return { ...claim, status: normalizeStatus(claim.status) };
  }

  async cancelCustomerRefund(id: string, reason: string, signal?: AbortSignal) {
    const claim = await this.request<CustomerRefundClaim>(
      `/customer-refund-claims/${encodeURIComponent(id)}/cancel`,
      { method: "POST", body: { reason }, signal },
    );
    this.invalidateCustomerRefund(id, claim.projectId);
    return { ...claim, status: normalizeStatus(claim.status) };
  }

  async recordCustomerRefundPayment(
    id: string,
    input: { amount: number; paidAt: string; transferReference: string },
    signal?: AbortSignal,
  ) {
    const claim = await this.financialRequest<CustomerRefundClaim>(
      `/customer-refund-claims/${encodeURIComponent(id)}/payments`,
      { method: "POST", body: input, signal },
    );
    this.invalidateCustomerRefund(id, claim.projectId);
    return { ...claim, status: normalizeStatus(claim.status) };
  }

  private invalidateCustomerRefund(id: string, projectId?: string) {
    this.invalidate("/customer-receipts");
    this.invalidate("/customer-refund-claims");
    this.invalidate("/admin/customer-refund-approvals");
    this.invalidate(`/customer-refund-claims/${encodeURIComponent(id)}`);
    if (projectId) this.invalidate(`/projects/${encodeURIComponent(projectId)}`);
    this.invalidate("/projects");
    this.invalidate("/dashboard");
  }

  async paymentObligations(projectId?: string, signal?: AbortSignal) {
    const path = this.query("/vendor-payment-obligations", { projectId });
    const value = await this.request<Array<VendorPaymentObligation & { amount?: number; remainingRequestable?: number; bank?: string }> | { items?: Array<VendorPaymentObligation & { amount?: number; remainingRequestable?: number; bank?: string }> }>(path, {
      signal,
      cacheTtlMs: 10_000,
    });
    return this.items(value).map((item) => ({
      ...item,
      totalAmount: item.totalAmount ?? item.amount ?? 0,
      remainingAmount: item.remainingAmount ?? item.remainingRequestable ?? 0,
      bankName: item.bankName ?? item.bank,
      status: normalizeStatus(item.status),
    }));
  }

  async payoutRequests(
    query: { projectId?: string; status?: string; month?: string; cursor?: string; limit?: number } = {},
    signal?: AbortSignal,
  ) {
    const path = this.query("/vendor-payment-requests", {
      ...query,
      status: query.status?.toLowerCase(),
      limit: query.limit ?? 30,
    });
    const value = await this.request<VendorPaymentRequestPage | Array<VendorPaymentRequest & { paymentStage?: string; bank?: string }>>(path, { signal, cacheTtlMs: 10_000 });
    const page = Array.isArray(value) ? { items: value } : value;
    return {
      ...page,
      items: (page.items ?? []).map((request) => {
        const raw = request as VendorPaymentRequest & { paymentStage?: string; bank?: string };
        return {
          ...request,
          stage: request.stage ?? raw.paymentStage,
          stageKind: normalizeStatus(request.stageKind),
          bankName: request.bankName ?? raw.bank,
          status: normalizeStatus(request.status),
          registrationNotificationStatus: normalizeStatus(request.registrationNotificationStatus),
          dailyDeliveryStatus: normalizeStatus(request.dailyDeliveryStatus),
        };
      }),
    };
  }

  async confirmVendorWorkCompletion(
    id: string,
    input: { completed: boolean; completedOn?: string; reason?: string },
    signal?: AbortSignal,
  ) {
    const result = await this.request<{ id: string; workCompleted: boolean; workCompletedOn?: string }>(
      `/vendor-payment-obligations/${encodeURIComponent(id)}/work-completion`,
      { method: "POST", body: input, signal },
    );
    this.invalidate("/vendor-payment-obligations");
    this.invalidate("/manager-settlements");
    this.invalidate("/projects");
    this.invalidate("/dashboard");
    return result;
  }

  async retryBotNotification(id: string, signal?: AbortSignal) {
    return this.request<{ ok: true }>(`/bot-notifications/${encodeURIComponent(id)}/retry`, {
      method: "POST",
      signal,
    });
  }

  async createPayoutRequest(input: CreateVendorPaymentRequestInput, signal?: AbortSignal) {
    const request = await this.request<VendorPaymentRequest>("/vendor-payment-requests", {
      method: "POST",
      body: {
        obligationId: input.obligationId,
        paymentMethod: input.paymentMethod,
        stageKind: input.stageKind,
        amount: input.amount,
        advanceReasonCode: input.advanceReasonCode,
        advanceReasonDetail: input.advanceReasonDetail,
        requestedAt: new Date().toISOString(),
      },
      signal,
    });
    this.invalidate("/vendor-payment");
    this.invalidate("/projects");
    this.invalidate("/dashboard");
    return { ...request, status: normalizeStatus(request.status) };
  }

  async submitPayoutRequest(id: string, signal?: AbortSignal) {
    const request = await this.request<VendorPaymentRequest>(`/vendor-payment-requests/${encodeURIComponent(id)}/submit`, {
      method: "POST",
      signal,
    });
    this.invalidate("/vendor-payment");
    this.invalidate("/projects");
    this.invalidate("/dashboard");
    return { ...request, status: normalizeStatus(request.status) };
  }

  bankPaymentCapabilities(signal?: AbortSignal) {
    return this.request<BankPaymentCapabilities>("/bank-payment-capabilities", {
      signal,
      cacheTtlMs: 3_000,
    });
  }

  bankTransactions(
    query: { from?: string; to?: string; matchStatus?: "unmatched" | "held" | "matched" | "classified"; limit?: number } = {},
    signal?: AbortSignal,
  ) {
    return this.request<BankTransactionLedger>(
      this.query("/bank-transactions", { ...query, limit: query.limit ?? 500 }),
      { signal, cacheTtlMs: 10_000 },
    );
  }

  async importBankCaptures(accountId: string, files: File[]): Promise<BankCaptureResult> {
    const body = new FormData();
    for (const file of files) body.append("files", file);
    const response = await fetch(this.url(`/bank-captures/${encodeURIComponent(accountId)}`), {
      method: "POST", credentials: "include", body, headers: { Accept: "application/json" },
    });
    const payload = await response.json();
    if (!response.ok) throw new ApiError(payload.message ?? "캡처 처리 결과를 확인하지 못했습니다.", response.status, payload);
    const result = (payload.data ?? payload) as BankCaptureResult;
    this.invalidate("/bank-transactions");
    this.invalidate("/dashboard");
    return result;
  }

  async createBankTransactionClassification(name: string, signal?: AbortSignal) {
    const classification = await this.financialRequest<BankTransactionClassification>(
      "/bank-transaction-classifications",
      { method: "POST", body: { name }, signal },
    );
    this.invalidate("/bank-transactions");
    return classification;
  }

  async deleteBankTransactionClassification(classificationId: string, signal?: AbortSignal) {
    const result = await this.financialRequest<{ ok: true }>(
      `/bank-transaction-classifications/${encodeURIComponent(classificationId)}`,
      { method: "DELETE", signal },
    );
    this.invalidate("/bank-transactions");
    return result;
  }

  async classifyBankTransaction(
    transactionId: string,
    classificationId: string,
    signal?: AbortSignal,
  ) {
    const transaction = await this.financialRequest<BankTransaction>(
      `/bank-transactions/${encodeURIComponent(transactionId)}/classification`,
      { method: "PATCH", body: { classificationId }, signal },
    );
    this.invalidate("/bank-transactions");
    return transaction;
  }

  async matchBankTransaction(
    transactionId: string,
    input: {
      candidateKind:
        | "card_settlements" | "customer_receipts" | "vendor_payments" | "customer_refund_payments"
        | "vendor_refund_receipts" | "manager_settlements"
        | "project_receivable" | "project_receivable_round" | "vendor_payment_request";
      targetId: string;
      projectId?: string;
    },
    signal?: AbortSignal,
  ) {
    const transaction = await this.financialRequest<BankTransaction>(
      `/bank-transactions/${encodeURIComponent(transactionId)}/match`,
      { method: "POST", body: input, signal },
    );
    this.invalidate("/bank-transactions");
    this.invalidate("/projects");
    this.invalidate("/vendor-payment");
    this.invalidate("/dashboard");
    return transaction;
  }

  async multiMatchBankTransactions(
    input: {
      mode: "split" | "aggregate";
      transactionIds: string[];
      targets: Array<{ targetType: BankLedgerTargetType; targetId: string }>;
    },
    signal?: AbortSignal,
  ) {
    const result = await this.financialRequest<{ transactions: BankTransaction[] }>(
      "/bank-transactions/multi-match",
      { method: "POST", body: input, signal },
    );
    this.invalidate("/bank-transactions");
    this.invalidate("/projects");
    this.invalidate("/vendor-payment");
    this.invalidate("/dashboard");
    return result.transactions;
  }

  async unmatchBankTransaction(transactionId: string, reason: string, signal?: AbortSignal) {
    const transaction = await this.financialRequest<BankTransaction>(
      `/bank-transactions/${encodeURIComponent(transactionId)}/unmatch`,
      { method: "POST", body: { reason }, signal },
    );
    this.invalidate("/bank-transactions");
    this.invalidate("/projects");
    this.invalidate("/vendor-payment");
    this.invalidate("/dashboard");
    return transaction;
  }

  async resetBankTransactionDecision(transactionId: string, reason: string, signal?: AbortSignal) {
    const transaction = await this.financialRequest<BankTransaction>(
      `/bank-transactions/${encodeURIComponent(transactionId)}/reset`,
      { method: "POST", body: { reason }, signal },
    );
    this.invalidate("/bank-transactions");
    return transaction;
  }

  async holdBankTransaction(
    transactionId: string,
    held: boolean,
    note?: string,
    signal?: AbortSignal,
  ) {
    const transaction = await this.financialRequest<BankTransaction>(
      `/bank-transactions/${encodeURIComponent(transactionId)}/hold`,
      { method: "PATCH", body: { held, ...(note ? { note } : {}) }, signal },
    );
    this.invalidate("/bank-transactions");
    return transaction;
  }

  async bankPaymentOrders(sourceRecordIds: string[], signal?: AbortSignal) {
    if (!sourceRecordIds.length) return [];
    const result = await this.request<{ items: BankPaymentOrder[] }>(
      this.query("/bank-payment-orders", { sourceRecordIds: sourceRecordIds.join(",") }),
      { signal, cacheTtlMs: 3_000 },
    );
    return result.items;
  }

  async createBankPaymentOrder(
    sourceRecordId: string,
    signal?: AbortSignal,
  ) {
    const result = await this.financialRequest<BankPaymentOrder>("/bank-payment-orders", {
      method: "POST",
      body: { sourceRecordId },
      signal,
    });
    this.invalidate("/bank-payment-orders");
    return result;
  }

  async createBankPaymentBatch(
    sourceRecordIds: string[],
    signal?: AbortSignal,
  ) {
    const result = await this.financialRequest<BankPaymentBatch>("/bank-payment-batches", {
      method: "POST",
      body: { sourceRecordIds },
      signal,
    });
    this.invalidate("/bank-payment-orders");
    return result;
  }

  async recoverBankPaymentOrder(
    requestId: string,
    action: "requeue" | "cancel",
    signal?: AbortSignal,
  ) {
    const result = await this.financialRequest<BankPaymentOrder>(
      `/bank-payment-orders/${encodeURIComponent(requestId)}/recovery`,
      {
        method: "POST",
        body: {
          action,
          confirmation: "NO_TRANSFER_CONFIRMED",
        },
        signal,
      },
    );
    this.invalidate("/bank-payment-orders");
    return result;
  }

  async recoverBankPaymentBatch(
    batchId: string,
    action: "requeue" | "cancel",
    signal?: AbortSignal,
  ) {
    const result = await this.financialRequest<BankPaymentBatch>(
      `/bank-payment-batches/${encodeURIComponent(batchId)}/recovery`,
      {
        method: "POST",
        body: {
          action,
          confirmation: "NO_TRANSFER_CONFIRMED",
        },
        signal,
      },
    );
    this.invalidate("/bank-payment-orders");
    return result;
  }

  async resetBankPaymentOrdersToRequest(
    requestIds: string[],
    signal?: AbortSignal,
  ) {
    const result = await this.financialRequest<{ items: BankPaymentOrder[] }>(
      "/bank-payment-orders/reset-to-request",
      {
        method: "POST",
        body: {
          requestIds,
          action: "reset_to_request",
          confirmation: "NO_TRANSFER_CONFIRMED",
        },
        signal,
      },
    );
    this.invalidate("/bank-payment-orders");
    return result.items;
  }

  bankPaymentEventStreamUrl() {
    return this.url("/bank-payment-events/stream");
  }

  createBankMirrorSession(signal?: AbortSignal) {
    return this.request<BankMirrorSession>("/bank-mirror/sessions", {
      method: "POST",
      body: { scope: "payouts" },
      signal,
    });
  }

  bankMirrorWebSocketUrl(sessionId: string) {
    const httpUrl = this.url(this.query("/bank-mirror/ws", { sessionId }));
    if (/^https?:\/\//i.test(httpUrl)) {
      return httpUrl.replace(/^http/i, "ws");
    }
    const location = globalThis.location;
    const protocol = location?.protocol === "https:" ? "wss:" : "ws:";
    const host = location?.host || "localhost";
    return `${protocol}//${host}${httpUrl}`;
  }

  async setBankAutomationEnabled(enabled: boolean, signal?: AbortSignal) {
    const result = await this.request<{ dispatchEnabled: boolean; updatedAt: string }>(
      "/bank-automation/control",
      { method: "POST", body: { enabled }, signal },
    );
    this.invalidate("/bank-payment-capabilities");
    return result;
  }

  async completionPhotos(id: string,signal?: AbortSignal) {
    const result = await this.request<{ items: CompletionPhoto[] }>(`/vendor-payment-requests/${encodeURIComponent(id)}/completion-photos`,{ signal });
    return result.items.map((item) => ({ ...item,archiveStatus: normalizeStatus(item.archiveStatus) }));
  }

  async projectCompletionPhotos(projectId: string,signal?: AbortSignal) {
    const result = await this.request<{ items: ProjectCompletionPhoto[] }>(
      `/projects/${encodeURIComponent(projectId)}/completion-photos`,
      { signal },
    );
    return result.items.map((item) => ({ ...item,archiveStatus: normalizeStatus(item.archiveStatus) }));
  }

  completionPhotoFileUrl(id: string) {
    return this.url(`/completion-photos/${encodeURIComponent(id)}/file`);
  }

  async uploadCompletionPhotos(id: string,files: File[],signal?: AbortSignal) {
    const form = new FormData();
    files.forEach((file) => form.append("photos",file,file.name));
    let response: Response;
    try {
      response = await fetch(this.url(`/vendor-payment-requests/${encodeURIComponent(id)}/completion-photos`),{
        method: "POST",body: form,credentials: "include",signal,headers: { Accept: "application/json" },
      });
    } catch (error) {
      if (error instanceof DOMException && error.name==="AbortError") throw error;
      throw new ApiError("서버에 연결할 수 없습니다.",0,error);
    }
    const payload = await response.json().catch(() => ({})) as { message?: string;items?: CompletionPhoto[] };
    if (!response.ok) throw new ApiError(payload.message ?? "완료사진을 등록하지 못했습니다.",response.status,payload);
    this.invalidate("/vendor-payment");
    this.invalidate("/projects");
    return payload.items ?? [];
  }

  async sendCompletionPhotos(id: string,assetIds?: string[],signal?: AbortSignal) {
    return this.request<{ id: string;status: string;recipientName: string;recipientPhone: string;photoCount: number }>(
      `/vendor-payment-requests/${encodeURIComponent(id)}/completion-deliveries`,
      { method: "POST",body: assetIds ? { assetIds } : {},signal },
    );
  }

  async vendorRefundClaims(projectId?: string,signal?: AbortSignal) {
    const value = await this.request<{ items: VendorRefundClaim[] }>(this.query("/vendor-refund-claims",{ projectId }),{ signal,cacheTtlMs: 5_000 });
    return value.items.map((item) => ({ ...item,status: normalizeStatus(item.status) }));
  }

  parseVendorRefund(raw: string,signal?: AbortSignal) {
    return this.request<{
      parsed?: { occurredAt?: string|null;depositor?: string|null;amount?: number|null };
      fingerprint?: string;missingFields?: string[];notification?: { id?: string }|null;
    }>("/vendor-refunds/parse",{ method: "POST",body: { raw },signal }).then((result): ParsedDepositNotification => ({
      rawText: raw,notificationId: result.notification?.id,
      depositedAt: result.parsed?.occurredAt ?? undefined,payer: result.parsed?.depositor ?? undefined,
      amount: result.parsed?.amount ?? undefined,bankName: "우리은행",fingerprint: result.fingerprint,
      warnings: result.missingFields?.map((field) => `${field} 확인 필요`),
    }));
  }

  async recordVendorRefund(claimId: string,input: { notificationId?: string;rawText?: string;fingerprint?: string;receivedAt?: string;depositor?: string;amount?: number },signal?: AbortSignal) {
    const value = await this.financialRequest(`/vendor-refund-claims/${encodeURIComponent(claimId)}/receipts`,{ method: "POST",body: input,signal });
    this.invalidate("/vendor-refund-claims");
    return value;
  }

  async cancelPayoutRequest(id: string, reason: string, signal?: AbortSignal) {
    const request = await this.request<VendorPaymentRequest>(`/vendor-payment-requests/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: { reason },
      signal,
    });
    this.invalidate("/vendor-payment");
    this.invalidate("/projects");
    this.invalidate("/dashboard");
    return request;
  }

  async markPayoutPaid(
    id: string,
    amount: number,
    paidAt: string,
    bankResolution?: BankTransferResolution,
    signal?: AbortSignal,
  ) {
    const request = await this.financialRequest<VendorPaymentRequest>(`/vendor-payment-requests/${encodeURIComponent(id)}/payments`, {
      method: "POST",
      body: { amount, paidAt, ...(bankResolution ? { bankResolution } : {}) },
      signal,
    });
    this.invalidate("/vendor-payment");
    this.invalidate("/projects");
    this.invalidate("/dashboard");
    return request;
  }

  async smsMessages(
    query: { status?: string; cursor?: string; limit?: number } = {},
    signal?: AbortSignal,
  ) {
    const path = this.query("/sms-messages", {
      status: query.status?.toLowerCase(),
      cursor: query.cursor,
      limit: query.limit ?? 30,
    });
    const page = await this.request<SmsMessagePage>(path, { signal, cacheTtlMs: 5_000 });
    return {
      ...page,
      items: (page.items ?? []).map((message) => ({ ...message, status: normalizeStatus(message.status) })),
    };
  }

  smsPrefill(projectId: string, signal?: AbortSignal) {
    return this.request<SmsPrefill>(this.query("/sms-messages/prefill", { projectId }), { signal });
  }

  async createSmsDraft(input: SmsDraftInput, signal?: AbortSignal) {
    const message = await this.request<SmsMessage>("/sms-messages", {
      method: "POST",
      body: input,
      signal,
    });
    this.invalidate("/sms-messages");
    this.invalidate("/dashboard");
    return { ...message, status: normalizeStatus(message.status) };
  }

  async updateSmsDraft(id: string, input: SmsDraftInput, signal?: AbortSignal) {
    const message = await this.request<SmsMessage>(`/sms-messages/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: input,
      signal,
    });
    this.invalidate("/sms-messages");
    this.invalidate("/dashboard");
    return { ...message, status: normalizeStatus(message.status) };
  }

  async approveSms(id: string, signal?: AbortSignal) {
    const message = await this.request<SmsMessage>(`/sms-messages/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      signal,
    });
    this.invalidate("/sms-messages");
    this.invalidate("/dashboard");
    return { ...message, status: normalizeStatus(message.status) };
  }

  async cancelSms(id: string, signal?: AbortSignal) {
    const message = await this.request<SmsMessage>(`/sms-messages/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      signal,
    });
    this.invalidate("/sms-messages");
    this.invalidate("/dashboard");
    return { ...message, status: normalizeStatus(message.status) };
  }

  async retrySms(id: string, signal?: AbortSignal) {
    const message = await this.request<SmsMessage>(`/sms-messages/${encodeURIComponent(id)}/retry`, {
      method: "POST",
      signal,
    });
    this.invalidate("/sms-messages");
    this.invalidate("/dashboard");
    return { ...message, status: normalizeStatus(message.status) };
  }

  async projectSettlements(projectId: string, signal?: AbortSignal) {
    const path = this.query("/manager-settlements", { projectId });
    const value = await this.request<ManagerSettlement[] | { items?: ManagerSettlement[] }>(path, {
      signal,
      cacheTtlMs: 15_000,
    });
    return this.items(value).map((settlement) => ({
      ...settlement,
      paidAt: settlement.paidAt ?? (settlement as ManagerSettlement & { paidOn?: string }).paidOn,
    }));
  }

  monthlyProfit(month: string, signal?: AbortSignal) {
    return this.request<import("./monthlyProfitTypes").MonthlyProfitReport>(this.query("/finance/monthly-profit", { month }), { signal, cacheTtlMs: 10_000 });
  }

  employeeSettlementReport(month: string, signal?: AbortSignal) {
    return this.request<EmployeeSettlementReport>(this.query("/manager-settlements/report", { month, includeReceiptBasis: "true" }), {
      signal,
      cacheTtlMs: 10_000,
    });
  }

  async dashboard(month: string, signal?: AbortSignal) {
    const path = this.query("/dashboard", { month });
    const [raw, employeeSettlements, closings] = await Promise.all([
      this.request<{
        month: string;
        confirmedQuotes: number;
        customerReceipts: number;
        customerRefunds: number;
        vendorPayments: number;
        openingReceivable: number;
        openingPayable: number;
        monthEndReceivable: number;
        monthEndPayable: number;
        baseMargin: number;
        managerLabor: number;
        finalExpectedMargin: number;
        selmoProfit: number;
        companyRetainedMargin: number;
        managerSettlementsPaid: number;
        cardFeeAmount?: number;
        actualCashMargin: number;
        monthlyProjects: MonthlyProjectActivity[];
      }>(path, { signal, cacheTtlMs: 30_000 }),
      this.employeeSettlementReport(month, signal),
      this.request<{ items?: Array<{ month: string; status: string; closedAt?: string }> }>("/month-closings", { signal, cacheTtlMs: 30_000 }),
    ]);
    const closing = closings.items?.find((item) => item.month.slice(0, 7) === month && item.status === "closed");
    return {
      month: raw.month,
      confirmedQuoteTotal: raw.confirmedQuotes,
      customerReceiptTotal: raw.customerReceipts,
      customerRefundTotal: raw.customerRefunds,
      vendorPaymentTotal: raw.vendorPayments,
      openingReceivable: raw.openingReceivable,
      openingPayable: raw.openingPayable,
      receivableBalance: raw.monthEndReceivable,
      payableBalance: raw.monthEndPayable,
      baseMargin: raw.baseMargin,
      managerLaborTotal: raw.managerLabor,
      expectedMargin: raw.finalExpectedMargin,
      selmoProfit: raw.selmoProfit,
      companyRetainedMargin: raw.companyRetainedMargin,
      managerSettlementsPaid: raw.managerSettlementsPaid ?? 0,
      cardFeeAmount: raw.cardFeeAmount??0,
      actualCashMargin: raw.actualCashMargin,
      monthlyProjects: (raw.monthlyProjects ?? []).map((project) => ({
        ...project,
        status: normalizeStatus(project.status),
      })),
      settlements: employeeSettlements.employees.flatMap((employee) =>
        employee.details
          .filter((detail) => detail.id !== null)
          .map((detail) => ({
            id: detail.id!,
            projectId: detail.projectId,
            projectName: detail.projectName,
            managerId: employee.managerId,
            managerName: employee.managerName,
            role: detail.role,
            rate: detail.rate,
            amount: detail.amount,
            settlementMonth: detail.settlementMonth ?? month,
            payoutMonth: detail.payoutMonth ?? month,
            settlementType: detail.settlementType ?? undefined,
            status: detail.status,
            paidAt: detail.paidOn ?? undefined,
          })),
      ),
      employeeSettlements,
      closed: Boolean(closing),
      closedAt: closing?.closedAt,
    } satisfies DashboardSummary;
  }

  dashboardOverview(
    date: string,
    scope: { managerId?: string; view?: "all" } = {},
    signal?: AbortSignal,
  ) {
    const path = this.query("/dashboard/overview", { date, ...scope });
    return this.request<DashboardOverview>(path, { signal, cacheTtlMs: 15_000 });
  }

  dashboardSchedule(
    month: string,
    scope: { managerId?: string; view?: "all" } = {},
    signal?: AbortSignal,
  ) {
    const path = this.query("/dashboard/schedule", { month, ...scope });
    return this.request<DashboardSchedule>(path, { signal, cacheTtlMs: 30_000 });
  }

  dashboardScheduleAttention(
    date: string,
    scope: { managerId?: string; view?: "all" } = {},
    signal?: AbortSignal,
  ) {
    const path = this.query("/dashboard/schedule-attention", { date, ...scope });
    return this.request<DashboardScheduleAttention>(path, { signal, cacheTtlMs: 15_000 });
  }

  dashboardScheduleRange(
    from: string,
    to: string,
    scope: { managerId?: string; view?: "all" } = {},
    signal?: AbortSignal,
  ) {
    const path = this.query("/dashboard/schedule", { from, to, ...scope });
    return this.request<DashboardSchedule>(path, { signal, cacheTtlMs: 30_000 });
  }

  async generateSettlements(month: string, signal?: AbortSignal) {
    const value = await this.request<{ items?: ManagerSettlement[] }>("/manager-settlements/generate", {
      method: "POST",
      body: { month },
      signal,
    });
    this.invalidate("/manager-settlements");
    this.invalidate("/dashboard");
    return value.items ?? [];
  }

  async markSettlementPaid(id: string, paidOn: string, signal?: AbortSignal) {
    const value = await this.financialRequest<ManagerSettlement>(`/manager-settlements/${encodeURIComponent(id)}/pay`, {
      method: "POST",
      body: { paidOn },
      signal,
    });
    this.invalidate("/manager-settlements");
    this.invalidate("/dashboard");
    return value;
  }

  async payMonthlySettlements(managerId: string, month: string, paidOn: string, signal?: AbortSignal) {
    const value = await this.financialRequest<{
      managerId: string;
      month: string;
      status: "paid";
      paidOn: string;
      amount: number;
      settlementIds: string[];
      alreadyPaid: boolean;
    }>("/manager-settlements/pay-month", {
      method: "POST",
      body: { managerId, month, paidOn },
      signal,
    });
    this.invalidate("/manager-settlements");
    this.invalidate("/dashboard");
    return value;
  }

  async correctManagerPayment(input: import("./types").ManagerPaymentCorrectionInput, signal?: AbortSignal) {
    const value = await this.financialRequest<{ status: "pending"; amount: number; settlementIds: string[] }>(
      "/manager-settlements/correct-payment", { method: "POST", body: input, signal });
    this.invalidate("/manager-settlements");
    this.invalidate("/dashboard");
    return value;
  }

  async closePayrollMonth(month: string, mode: "paid" | "rollover", signal?: AbortSignal) {
    const result = await this.request<{
      month: string;
      status: "closed";
      mode: "paid" | "rollover";
      rolloverMonth: string | null;
      rolledOverAmount: number;
      operatingManagementFeeAmount: number;
    }>("/payroll-month-closings", {
      method: "POST",
      body: { month, mode },
      signal,
    });
    this.invalidate("/manager-settlements");
    this.invalidate("/dashboard");
    return result;
  }

  async closeMonth(month: string, signal?: AbortSignal) {
    const result = await this.request<{ month: string; closed: boolean }>("/month-closings", {
      method: "POST",
      body: { month },
      signal,
    });
    this.invalidate("/dashboard");
    this.invalidate("/month-closings");
    return result;
  }

  improvements(status?: ImprovementStatus, signal?: AbortSignal) {
    const path = this.query("/improvements", { status, limit: 200 });
    return this.request<{ items: ImprovementRequest[] }>(path, { signal, cacheTtlMs: 5_000 });
  }

  improvement(id: string, signal?: AbortSignal) {
    return this.request<ImprovementRequest>(`/improvements/${encodeURIComponent(id)}`, {
      signal,
      cacheTtlMs: 2_000,
    });
  }

  async createImprovement(input: {
    title: string;
    body: string;
    category: ImprovementRequest["category"];
    visibility: ImprovementRequest["visibility"];
    priority: ImprovementRequest["priority"];
    sourceRoute: string;
    sourceQuery?: string;
    sourceFeedbackId?: string;
    sourceX?: number;
    sourceY?: number;
    viewportWidth?: number;
    viewportHeight?: number;
    sourceBuildSha?: string;
  }, signal?: AbortSignal) {
    const result = await this.request<ImprovementRequest>("/improvements", {
      method: "POST",
      body: input,
      signal,
    });
    this.invalidate("/improvements");
    return result;
  }

  async updateImprovement(id: string, input: {
    title: string;
    body: string;
    category: ImprovementRequest["category"];
    visibility: ImprovementRequest["visibility"];
    editReason?: string;
  }, signal?: AbortSignal) {
    const result = await this.request<ImprovementRequest>(`/improvements/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: input,
      signal,
    });
    this.invalidate("/improvements");
    return result;
  }

  async addImprovementComment(id: string, body: string, signal?: AbortSignal) {
    const result = await this.request<unknown>(`/improvements/${encodeURIComponent(id)}/comments`, {
      method: "POST",
      body: { body },
      signal,
    });
    this.invalidate("/improvements");
    return result;
  }

  async setImprovementStatus(
    id: string,
    status: "RECEIVED" | "NEEDS_RESPONSE" | "RESOLVED" | "ON_HOLD" | "REJECTED" | "DUPLICATE" | "WITHDRAWN",
    reason: string,
    signal?: AbortSignal,
  ) {
    const result = await this.request<ImprovementRequest>(
      `/admin/improvements/${encodeURIComponent(id)}/status`,
      { method: "POST", body: { status, reason }, signal },
    );
    this.invalidate("/improvements");
    return result;
  }

  improvementAttachmentUrl(id: string, attachmentId: string) {
    return this.url(
      `/improvements/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`,
    );
  }

  async uploadImprovementAttachment(id: string, file: File, signal?: AbortSignal) {
    const body = new FormData();
    body.append("file", file);
    const response = await fetch(this.url(`/improvements/${encodeURIComponent(id)}/attachments`), {
      method: "POST",
      credentials: "include",
      body,
      signal,
      headers: { Accept: "application/json" },
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new ApiError(
        typeof payload === "object" && payload && "message" in payload
          ? String(payload.message)
          : "첨부파일을 저장하지 못했습니다.",
        response.status,
        payload,
      );
    }
    this.invalidate("/improvements");
    return payload as { id: string; request: ImprovementRequest; expiresAt: string };
  }

  changeJournal(signal?: AbortSignal) {
    return this.request<{ items: ChangeJournalEntry[] }>("/change-journal?limit=200", {
      signal,
      cacheTtlMs: 10_000,
    });
  }

  rollbackRequests(signal?: AbortSignal) {
    return this.request<{ items: RollbackRequest[] }>("/admin/rollback-requests?limit=100", {
      signal,
      cacheTtlMs: 5_000,
    });
  }

  async createRollbackRequest(
    targetReleaseId: string,
    reason: string,
    signal?: AbortSignal,
  ) {
    const result = await this.request<RollbackRequest>("/admin/rollback-requests", {
      method: "POST",
      body: { targetReleaseId, reason },
      signal,
    });
    this.invalidate("/change-journal");
    this.invalidate("/admin/rollback-requests");
    return result;
  }

  async cancelRollbackRequest(id: string, reason: string, signal?: AbortSignal) {
    const result = await this.request<RollbackRequest>(
      `/admin/rollback-requests/${encodeURIComponent(id)}/cancel`,
      { method: "POST", body: { reason }, signal },
    );
    this.invalidate("/change-journal");
    this.invalidate("/admin/rollback-requests");
    return result;
  }

  improvementPrincipals(signal?: AbortSignal) {
    return this.request<{ items: ImprovementPrincipal[] }>("/admin/improvement-principals", {
      signal,
      cacheTtlMs: 5_000,
    });
  }

  async createCodexAutomationPrincipal(expiresInDays = 30, signal?: AbortSignal) {
    const result = await this.request<{
      principal: ImprovementPrincipal;
      token: string;
      tokenPrefix: string;
      expiresAt: string;
    }>("/admin/improvement-principals", {
      method: "POST",
      body: { expiresInDays },
      signal,
    });
    this.invalidate("/admin/improvement-principals");
    return result;
  }

  async setImprovementPrincipalEnabled(id: string, enabled: boolean, signal?: AbortSignal) {
    const result = await this.request<{ id: string; enabled: boolean }>(
      `/admin/improvement-principals/${encodeURIComponent(id)}`,
      { method: "PATCH", body: { enabled }, signal },
    );
    this.invalidate("/admin/improvement-principals");
    return result;
  }

}

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "/api/v1";

export const api = new ApiClient(apiBaseUrl);
